import React, { useState, useEffect } from "react";
import { useNavigate, Routes, Route, Navigate, Link, useLocation } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { useRealtime } from "../hooks/useRealtime";
import {
  Package, LogOut, Search, Save, Calendar, Truck, AlertTriangle, BarChart3, Building2, ShoppingCart,
  CheckCircle2, ClipboardList, ChevronRight, X, Boxes, Plus, Download, MessageCircle, FileText, Eye, Sparkles, ScanLine, Receipt, Video, Share2, FolderOpen, Check,
  Menu, PanelLeftClose, PanelLeftOpen, PackagePlus, ArrowRightLeft, ChevronDown, ExternalLink, Inbox, Settings as SettingsIcon, Tag
} from "lucide-react";
import WebmailPage from "../components/WebmailPage";
import EmailSettingsCard from "../components/EmailSettingsCard";
import InventoryShipments from "../pages/warehouse/InventoryShipments";
import InventoryLots from "../pages/warehouse/InventoryLots";
import WmsGiacenze from "../pages/wms/WmsGiacenze";
import WmsMovimenti from "../pages/wms/WmsMovimenti";
import NumberInput from "../components/NumberInput";
import { ChatButton, ChatDrawer } from "../components/ChatDrawer";
import { InstallPWAButton } from "../components/InstallPWAButton";
import { WarehouseOffersTab } from "../components/OffersComponents";
import DedicatedOffersList from "../components/DedicatedOffersList";
import { SuppliersTab, SupplierOrdersTab } from "../components/SuppliersComponents";
import StatementsPage from "../pages/StatementsPage";
import DocumentsPage from "../pages/DocumentsPage";
import AdminCustomersManagement from "../pages/admin/CustomersManagement";
import WarehousePricelistsView from "./WarehousePricelistsView";
import TutorialsPage from "../pages/TutorialsPage";
import OrderRowActions from "../components/OrderRowActions";
import { changeLabel } from "../components/OrderChangeRequestDialog";
import { ChangeCustomerDialog } from "../components/ChangeCustomerDialog";
import DDTCameraScanner from "../components/DDTCameraScanner";
import { EtiflorDdtPanel } from "../components/warehouse/EtiflorDdtPanel";
import { DeliveryBadges, DeliveryCard } from "../components/logistics/DeliveryInfo";
import LogisticsPage from "../pages/LogisticsPage";
import DdtStandaloneDialog from "../components/DdtStandaloneDialog";
import LabelsPrintDialog from "../components/LabelsPrintDialog";
import { openAuthenticatedPdf, openAuthenticatedPdfPost, orderPdfFilename } from "../utils/pdf";
import { DatePickerIT } from "../components/DatePickerIT";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Card } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { FieldHint } from "../components/ui/FieldHint";
import { useLang } from "../context/LangContext";
import { useCompany } from "../context/CompanyContext";
import { formatMoney, currencySymbol } from "../utils/currency";

const useFmt = () => {
  const { company, displayMode } = useCompany();
  return (v) => formatMoney(v, company, { displayMode });
};

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const statusLabelMap = (t) => ({
  pending_agent: t.statusPendingAgent,
  pending: t.statusPending,
  confirmed: t.statusConfirmed,
  in_preparation: t.statusInPrep,
  ready: t.statusReady,
  shipped: t.statusShipped,
  delivered: t.statusDelivered,
  completed: t.statusCompleted,
  cancelled: t.statusCancelled,
  postponed: "⏸ Posticipato",
});
const STATUS_COLOR = {
  draft: "bg-stone-200 text-stone-700",
  pending_agent: "bg-amber-100 text-amber-800",
  pending: "bg-yellow-100 text-yellow-700",
  confirmed: "bg-blue-100 text-blue-700",
  in_preparation: "bg-orange-100 text-orange-700",
  ready: "bg-cyan-100 text-cyan-700",
  shipped: "bg-purple-100 text-purple-700",
  delivered: "bg-emerald-100 text-emerald-700",
  completed: "bg-green-100 text-green-700",
  cancelled: "bg-red-100 text-red-700",
  postponed: "bg-slate-100 text-slate-700",
};
const AV_COLOR = {
  pending: "bg-stone-100 text-stone-700",
  delivered: "bg-green-100 text-green-700",
  missing: "bg-red-100 text-red-700",
  partial: "bg-orange-100 text-orange-700",
};

const Logo = () => (
  <img src="https://customer-assets.emergentagent.com/job_sales-catalog-hub-1/artifacts/6bi5tskl_giotto-hor-bordo-carni%20%282%29.png" alt="Giotto" className="h-10" />
);

// Sezione richiudibile solo su mobile: su desktop il contenuto è sempre visibile
const MobileCollapse = ({ title, children, testid }) => {
  const [open, setOpen] = useState(false);
  return (
    <div className="space-y-2 md:space-y-0 md:contents">
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className="md:hidden w-full flex items-center justify-between bg-stone-100 border border-stone-200 rounded-lg px-3 py-2.5 text-sm font-semibold text-stone-800"
        data-testid={testid}
      >
        <span>{title}</span>
        <ChevronDown className={`w-4 h-4 transition-transform ${open ? "rotate-180" : ""}`} />
      </button>
      <div className={`${open ? "block" : "hidden"} md:block space-y-4`}>{children}</div>
    </div>
  );
};

export const OrderEditModal = ({ order, onClose, onSaved, onOpenChat, onAdvanceCleared }) => {
  const { t } = useLang();
  const fm = useFmt();
  const [advRequired, setAdvRequired] = useState(!!order.advance_payment_required);
  const [advCleared, setAdvCleared] = useState(!!order.advance_payment_cleared);
  const [advClearedBy, setAdvClearedBy] = useState(order.advance_payment_cleared_by || "");
  const [unblocking, setUnblocking] = useState(false);
  const unblockAdvance = async () => {
    setUnblocking(true);
    try {
      const r = await axios.patch(`${API}/orders/${order.id}/advance-payment`, { required: false });
      setAdvRequired(false);
      setAdvCleared(true);
      setAdvClearedBy(r.data?.advance_payment_cleared_by || "");
      onAdvanceCleared?.(order.id);
      toast.success("Blocco pagamento anticipato rimosso per questo ordine");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Errore nello sblocco");
    } finally { setUnblocking(false); }
  };
  const [items, setItems] = useState(order.items.map(it => ({
    product_id: it.product_id,
    code: it.code,
    description: it.description,
    quantity: it.quantity,
    unit: it.unit,
    original_unit: it.unit,  // immutable copy for display in 'Ord.' column
    unit_price: it.unit_price,
    is_free: !!it.is_free,
    actual_quantity: it.actual_quantity ?? it.quantity,
    availability_status: it.availability_status || "delivered",
    warehouse_note: it.warehouse_note || "",
    vat_rate: it.vat_rate ?? 0.10,
    discount_percent: it.discount_percent ?? 0,
    package_count: it.package_count ?? 1,
  })));
  const [newItems, setNewItems] = useState([]);  // free items / catalog items added by warehouse
  const [extraCosts, setExtraCosts] = useState(order.extra_costs || []);
  const [changeCustOpen, setChangeCustOpen] = useState(false);
  const [shippingDate, setShippingDate] = useState(order.shipping_date || "");
  const [expectedDeliveryDate, setExpectedDeliveryDate] = useState(order.expected_delivery_date || "");
  const [documentReference, setDocumentReference] = useState(order.document_reference || "");
  const [warehouseNotes, setWarehouseNotes] = useState(order.warehouse_notes || "");
  const [totalPackages, setTotalPackages] = useState((order.total_packages ?? (order.items || []).reduce((s, it) => s + (parseInt(it.package_count) || 0), 0)) || 1);
  const [status, setStatus] = useState(order.status);
  const [saving, setSaving] = useState(false);
  const [itemsReplaced, setItemsReplaced] = useState(false); // true dopo "Sostituzione completa" da DDT scan → save invia replace_items
  const [ddtAttachment, setDdtAttachment] = useState(order.ddt_attachment || null);
  const [uploadingDdt, setUploadingDdt] = useState(false);
  const [catalogSearch, setCatalogSearch] = useState("");
  const [catalogResults, setCatalogResults] = useState([]);
  const [showExtraProductModal, setShowExtraProductModal] = useState(false);
  const [showDDTScanner, setShowDDTScanner] = useState(false);
  const [showAiScanner, setShowAiScanner] = useState(false);
  const isForeign = !!order.customer?.is_foreign;

  useEffect(() => {
    if (!catalogSearch || catalogSearch.length < 2) { setCatalogResults([]); return; }
    const t = setTimeout(async () => {
      try {
        const r = await axios.get(`${API}/products?search=${encodeURIComponent(catalogSearch)}&active_only=true`);
        setCatalogResults((r.data || []).slice(0, 8));
      } catch { setCatalogResults([]); }
    }, 250);
    return () => clearTimeout(t);
  }, [catalogSearch]);

  const addFromCatalog = (prod) => {
    setNewItems(prev => [...prev, {
      product_id: prod.id, code: prod.code, description: prod.description,
      quantity: 1, unit: prod.unit || "Kg", unit_price: 0,
      line_total: 0, is_free: false, vat_rate: prod.vat_rate ?? 0.10,
    }]);
    setCatalogSearch(""); setCatalogResults([]);
    toast.success(`${prod.code} aggiunto — imposta peso e prezzo`);
  };

  const updateItem = (idx, field, value) => {
    const arr = [...items];
    arr[idx] = { ...arr[idx], [field]: value };
    if (field === "availability_status" && value === "missing") {
      arr[idx].actual_quantity = 0;
    }
    setItems(arr);
  };

  const updateNewItem = (idx, field, value) => {
    const arr = [...newItems];
    arr[idx] = { ...arr[idx], [field]: value };
    arr[idx].line_total = +(arr[idx].quantity * arr[idx].unit_price).toFixed(2);
    setNewItems(arr);
  };

  const addEmptyNewItem = () => {
    setNewItems(prev => [...prev, {
      product_id: null, code: "", description: "", quantity: 1, unit: "Kg",
      unit_price: 0, line_total: 0, is_free: true, vat_rate: 0.10,
    }]);
  };

  const addEmptyExtraCost = () => {
    setExtraCosts(prev => [...prev, { description: "", amount: 0, vat_rate: 0.10 }]);
  };
  const updateExtraCost = (idx, field, value) => {
    const arr = [...extraCosts];
    arr[idx] = { ...arr[idx], [field]: value };
    setExtraCosts(arr);
  };

  // Calc totals (preview) — net of per-line discount
  const lineNet = (it) => (it.actual_quantity || 0) * (it.unit_price || 0) * (1 - ((it.discount_percent || 0) / 100));
  const itemsSubtotal = items.reduce((s, it) => s + (it.availability_status === "missing" ? 0 : lineNet(it)), 0);
  const newItemsSubtotal = newItems.reduce((s, it) => s + (it.quantity * it.unit_price), 0);
  const extraCostsSubtotal = extraCosts.reduce((s, ec) => s + (ec.amount || 0), 0);
  const subtotal = itemsSubtotal + newItemsSubtotal + extraCostsSubtotal;
  const vatTotal = isForeign ? 0 : (
    items.reduce((s, it) => s + (it.availability_status === "missing" ? 0 : (lineNet(it) * (it.vat_rate || 0))), 0) +
    newItems.reduce((s, it) => s + (it.quantity * it.unit_price * (it.vat_rate || 0)), 0) +
    extraCosts.reduce((s, ec) => s + ((ec.amount || 0) * (ec.vat_rate || 0)), 0)
  );
  const grandTotal = subtotal + vatTotal;
  const finalCommission = subtotal * (order.commission_rate || 0.09);

  // Totals kg (only "delivered/partial/pending" items count, not "missing"). totalPackages is now a global state above.
  const totalKg = items.reduce((s, it) => s + (it.availability_status === "missing" ? 0 : (it.actual_quantity || 0)), 0)
                + newItems.reduce((s, it) => s + (it.quantity || 0), 0);

  // Label printing dialog state
  const [showLabelsDialog, setShowLabelsDialog] = useState(false);

  // Availability status colors/labels (used by mobile card view)
  const availColors = {
    pending: "bg-stone-100 text-stone-600",
    delivered: "bg-green-100 text-green-700",
    partial: "bg-amber-100 text-amber-700",
    missing: "bg-red-100 text-red-700",
  };
  const availLabels = {
    pending: "In attesa",
    delivered: "Consegnato",
    partial: "Parziale",
    missing: "Mancante",
  };

  const pendingCRs = (order.change_requests || []).filter((r) => r.status === "pending");
  const resolveCR = async (reqId, action) => {
    let note = "";
    if (action === "reject") note = window.prompt("Motivo del rifiuto (facoltativo):") || "";
    try {
      await axios.post(`${API}/orders/${order.id}/change-requests/${reqId}/resolve`, { action, note });
      toast.success(action === "accept" ? "Richiesta accettata: ordine aggiornato ✅" : "Richiesta rifiutata");
      onSaved();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Errore gestione richiesta");
    }
  };

  const save = async () => {
    setSaving(true);
    try {
      const payload = {
        ...(itemsReplaced ? {
          // Sostituzione completa da lettura DDT: la lista items è autorevole
          replace_items: items.map(it => ({
            product_id: it.product_id || null,
            code: it.code || "",
            description: it.description || "",
            quantity: it.quantity ?? null,
            actual_quantity: it.actual_quantity,
            unit: it.unit || "Kg",
            unit_price: it.unit_price ?? null,
            vat_rate: it.vat_rate ?? null,
            discount_percent: it.discount_percent ?? 0,
            is_free: !!it.is_free,
            availability_status: it.availability_status || "delivered",
            warehouse_note: it.warehouse_note || "",
          })),
        } : {
          items: items.map((it, idx) => ({
            index: idx,
            product_id: it.product_id,
            code: it.is_free ? it.code : undefined,
            actual_quantity: it.actual_quantity,
            actual_unit: it.unit,
            actual_unit_price: it.unit_price,
            actual_description: it.is_free ? it.description : undefined,
            actual_code: it.is_free ? it.code : undefined,
            availability_status: it.availability_status,
            warehouse_note: it.warehouse_note,
            vat_rate: it.vat_rate,
            discount_percent: it.discount_percent ?? 0,
          })),
        }),
        new_items: newItems.filter(ni => ni.description && ni.quantity > 0).map(ni => ({
          ...ni, is_free: !ni.product_id,
          line_total: +(ni.quantity * ni.unit_price).toFixed(2),
        })),
        extra_costs: extraCosts.filter(ec => ec.description && ec.amount > 0),
        shipping_date: shippingDate || null,
        expected_delivery_date: expectedDeliveryDate || null,
        document_reference: documentReference || null,
        warehouse_notes: warehouseNotes,
        total_packages: Math.max(0, parseInt(totalPackages) || 0),
        status,
      };
      await axios.patch(`${API}/orders/${order.id}/warehouse`, payload);
      toast.success("Ordine aggiornato");
      onSaved();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Errore");
    } finally { setSaving(false); }
  };

  const downloadPDF = (forCustomer = false) => {
    openAuthenticatedPdf(`/orders/${order.id}/pdf?for_customer=${forCustomer}`, {
      download: true,
      filename: orderPdfFilename(order, { forCustomer }),
    });
  };

  const previewPDF = (forCustomer = false) => {
    openAuthenticatedPdf(`/orders/${order.id}/pdf?for_customer=${forCustomer}&inline=true`);
  };

  const shareWhatsApp = () => {
    const phone = (order.customer?.phone || "").replace(/[^0-9+]/g, "");
    const grand = (order.final_grand_total ?? order.grand_total ?? order.total_amount) || 0;
    const msg = encodeURIComponent(
      `*GIOTTO CARNI — Ordine ${order.order_number}*\n` +
      `Cliente: ${order.customer?.customer_name || ""}\n` +
      `Totale: ${fm(grand)}\n` +
      (shippingDate ? `Spedizione: ${shippingDate}\n` : "") +
      (expectedDeliveryDate ? `Consegna prevista: ${expectedDeliveryDate}\n` : "") +
      `Riceverai il documento d'ordine a breve. Grazie!`
    );
    const url = phone ? `https://wa.me/${phone}?text=${msg}` : `https://wa.me/?text=${msg}`;
    window.open(url, '_blank');
  };

  return (
    <Dialog open={true} onOpenChange={onClose}>
      <DialogContent className="max-w-5xl bg-white p-3 sm:p-6 min-h-[100dvh] max-h-[100dvh] sm:min-h-0 sm:max-h-[95vh] overflow-y-auto rounded-none sm:rounded-lg" data-testid="warehouse-edit-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 sm:gap-3 flex-wrap">
            <Boxes className="w-5 h-5 text-[#8A1C2A]" />
            <span>Preparazione ordine</span>
            <span className="font-mono text-[#8A1C2A]">{order.order_number}</span>
            <Badge className={STATUS_COLOR[order.status]}>{statusLabelMap(t)[order.status]}</Badge>
          </DialogTitle>
        </DialogHeader>

        {advRequired && (
          <div className="bg-red-600 text-white font-black text-lg sm:text-2xl text-center rounded-lg px-4 py-4 tracking-wider animate-pulse" data-testid="wh-advance-payment-banner">
            ⚠️ PAGAMENTO ANTICIPATO NECESSARIO ⚠️
            <p className="text-xs sm:text-sm font-semibold tracking-normal mt-1 text-red-100">Plafond assicurativo non concesso — incassare PRIMA della consegna</p>
            <Button onClick={unblockAdvance} disabled={unblocking} className="mt-3 bg-white text-red-700 hover:bg-red-50 font-bold text-sm tracking-normal" data-testid="wh-advance-unblock-btn">
              {unblocking ? "Sblocco…" : "✓ Sblocca ordine — pagamento ricevuto"}
            </Button>
          </div>
        )}
        {!advRequired && advCleared && (
          <div className="bg-emerald-50 border border-emerald-300 text-emerald-800 text-sm font-semibold rounded-lg px-4 py-2 text-center" data-testid="wh-advance-cleared-note">
            ✓ Pagamento anticipato SBLOCCATO{advClearedBy ? ` da ${advClearedBy}` : ""} — ordine lavorabile
          </div>
        )}

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm bg-stone-50 p-3 rounded">
          <div>
            <p className="font-semibold">{order.customer?.customer_name}</p>
            <p className="text-stone-600">{order.customer?.company}</p>
            <p className="text-xs text-stone-500">{order.customer?.address}, {order.customer?.city}</p>
            <p className="text-xs text-stone-500">Tel: {order.customer?.phone}</p>
          </div>
          <div>
            <p>Agente: <strong>{order.agent_name}</strong></p>
            <p>Data ordine: {new Date(order.created_at).toLocaleString('it-IT')}</p>
            {order.requested_shipping_date && (
              <p className="bg-amber-50 p-2 mt-1 rounded text-xs border-l-2 border-amber-400" data-testid="wh-requested-shipping-date">
                <strong>🚚 Consegna richiesta dall'agente:</strong> {new Date(order.requested_shipping_date).toLocaleDateString('it-IT')}
              </p>
            )}
            {order.customer?.special_requests && (
              <p className="bg-yellow-50 p-2 mt-1 rounded text-xs border-l-2 border-yellow-400"><strong>Richieste:</strong> {order.customer.special_requests}</p>
            )}
          </div>
        </div>

        {/* DDT sincronizzati da Etiflor: collegamento in un click (sostituisce la lettura AI) */}
        {!["cancelled", "draft", "pending_agent"].includes(order.status) && (
          <EtiflorDdtPanel order={order} onLinked={() => onSaved()} />
        )}

        {!["shipped", "delivered", "completed", "cancelled"].includes(order.status) && (
          <div>
            <Button size="sm" variant="outline" onClick={() => setChangeCustOpen(true)} className="border-stone-300 text-stone-700" data-testid="wh-change-customer-btn">
              👤 Cambia cliente
            </Button>
          </div>
        )}
        <ChangeCustomerDialog
          order={order}
          open={changeCustOpen}
          onClose={() => setChangeCustOpen(false)}
          onChanged={() => onSaved()}
        />

        {order.delivery_notes && (
          <div className="bg-amber-50 border-l-4 border-amber-400 p-3 rounded" data-testid="wh-order-delivery-notes">
            <p className="text-xs font-semibold text-amber-800 mb-1">📝 Note ordine / consegna (dall'agente)</p>
            <p className="text-sm text-stone-800 whitespace-pre-wrap">{order.delivery_notes}</p>
          </div>
        )}

        {pendingCRs.length > 0 && (
          <div className="border-2 border-purple-400 bg-purple-50 rounded-lg p-3 space-y-2" data-testid="wh-change-requests-pane">
            <p className="text-sm font-bold text-purple-900">🔁 Richiesta di modifica dall'agente {order.agent_name}</p>
            {pendingCRs.map((r) => (
              <div key={r.id} className="bg-white border border-purple-200 rounded p-2.5">
                <p className="text-[11px] text-stone-500">{new Date(r.requested_at).toLocaleString("it-IT")}</p>
                <ul className="text-xs text-stone-800 mt-1 space-y-0.5">
                  {r.changes.map((c, i) => <li key={i}>{changeLabel(c)}</li>)}
                </ul>
                {r.note && <p className="text-xs italic text-stone-600 mt-1">📝 {r.note}</p>}
                <div className="flex flex-wrap gap-2 mt-2">
                  <Button size="sm" onClick={() => resolveCR(r.id, "accept")} className="h-7 text-xs bg-emerald-600 hover:bg-emerald-700 text-white" data-testid={`wh-cr-accept-${r.id}`}>
                    <Check className="w-3 h-3 mr-1" /> Accetta e applica
                  </Button>
                  <Button size="sm" variant="outline" onClick={() => resolveCR(r.id, "reject")} className="h-7 text-xs border-red-300 text-red-600 hover:bg-red-50" data-testid={`wh-cr-reject-${r.id}`}>
                    ✕ Rifiuta
                  </Button>
                </div>
              </div>
            ))}
          </div>
        )}

        {["shipped", "delivered", "completed"].includes(order.status) && (
          <div className="bg-purple-50 border border-purple-300 rounded p-2.5 text-xs text-purple-900 flex items-center gap-2" data-testid="wh-shipped-edit-notice">
            <Truck className="w-4 h-4 shrink-0" />
            <span><strong>Ordine già spedito/concluso:</strong> puoi comunque modificarlo (es. resi o note di credito). Totali e documento di vendita collegato verranno aggiornati automaticamente.</span>
          </div>
        )}

        <MobileCollapse title="🚚 Spedizione, stato e Rif. DDT" testid="wh-collapse-shipping">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <div>
            <Label className="text-xs">🚚 Data spedizione</Label>
            <DatePickerIT value={shippingDate} onChange={setShippingDate} data-testid="warehouse-ship-date" />
          </div>
          <div>
            <Label className="text-xs">📅 Consegna prevista</Label>
            <DatePickerIT value={expectedDeliveryDate} onChange={setExpectedDeliveryDate} data-testid="warehouse-expected-delivery" />
          </div>
          <div>
            <Label className="text-xs flex items-center gap-1">📦 Colli totali ordine
              <FieldHint>
                <strong>Numero totale di colli (cartoni/pacchi)</strong> con cui esce l'intero ordine. È un valore unico per tutto l'ordine, non per singolo articolo. Apparirà sul PDF cliente e nei documenti di trasporto.
              </FieldHint>
            </Label>
            <NumberInput
              type="number"
              min="0"
              step="1"
              value={totalPackages}
              onChange={(e) => setTotalPackages(parseInt(e.target.value) || 0)}
              className="bg-white border-stone-300"
              data-testid="warehouse-total-packages"
            />
          </div>
          <div>
            <Label className="text-xs flex items-center gap-1">Stato ordine
              <FieldHint>
                <strong>Stato di evasione</strong> dell'ordine:<br/>
                ✅ <strong>Confermato</strong> — preso in carico, in lavorazione<br/>
                🚚 <strong>Spedito</strong> — uscito dal magazzino (richiede DDT)<br/>
                ⏸ <strong>Posticipato</strong> — rimandato a data successiva<br/>
                ❌ <strong>Annullato</strong> — non evaso (es. cliente disdice)
              </FieldHint>
            </Label>
            <Select value={status} onValueChange={setStatus}>
              <SelectTrigger className="bg-white border-stone-300" data-testid="warehouse-status-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="pending">⏳ In attesa</SelectItem>
                <SelectItem value="confirmed">✅ Confermato</SelectItem>
                <SelectItem value="shipped">🚚 Spedito</SelectItem>
                <SelectItem value="delivered">📦 Consegnato</SelectItem>
                <SelectItem value="completed">🏁 Completato</SelectItem>
                <SelectItem value="postponed">⏸ Posticipato</SelectItem>
                <SelectItem value="cancelled">❌ Annullato</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>

        {/* Logistica: corriere e data di consegna per il CAP dell'ordine, simulazione sulle date di spedizione */}
        <DeliveryCard
          cap={order.delivery_address?.cap || order.customer?.cap}
          localita={order.delivery_address?.city || order.customer?.city}
          orderId={order.id}
          shipDate={shippingDate || undefined}
          simulate={7}
          compact
          onPickDate={(d) => { setShippingDate(d); toast.success(`Data di spedizione impostata: ${new Date(d).toLocaleDateString("it-IT")} — ricordati di salvare`); }}
          testid="wh-delivery-card"
        />

        {/* Document reference (obbligatorio per stato Spedito) */}
        <div className={`p-3 rounded border ${status === "shipped" ? "bg-amber-50 border-amber-300" : "bg-stone-50 border-stone-200"}`}>
          <Label className="text-xs font-semibold flex items-center gap-1">📄 Riferimento documento (DDT interno) {status === "shipped" && <span className="text-red-600">*</span>}
            <FieldHint>
              <strong>Numero del DDT</strong> (Documento di Trasporto) interno con cui esce la merce. <strong>Obbligatorio</strong> quando lo stato diventa "Spedito" perché il cliente ne ha bisogno per controlli/ricezione.
            </FieldHint>
          </Label>
          <Input value={documentReference} onChange={e => setDocumentReference(e.target.value)} className="bg-white border-stone-300 mt-1" placeholder="Es. DDT-2026-0123 (numero interno)" data-testid="warehouse-document-reference" />
          <p className="text-[11px] text-stone-500 mt-1">Inserito quando l'ordine viene spedito. Sarà visibile sul PDF cliente e nelle viste agente/admin.</p>

          {/* Allegato: PDF DDT originale dell'altro gestionale */}
          <div className="mt-2 flex flex-wrap items-center gap-2" data-testid="warehouse-ddt-attachment-box">
            {ddtAttachment?.path ? (
              <>
                <button
                  onClick={() => window.open(`${API}/files/${ddtAttachment.path}`, "_blank")}
                  className="inline-flex items-center gap-1.5 text-xs bg-emerald-50 border border-emerald-200 text-emerald-800 rounded-full px-3 py-1.5 hover:bg-emerald-100"
                  data-testid="warehouse-ddt-attachment-open"
                >
                  📎 {ddtAttachment.filename || "DDT allegato"} — apri
                </button>
                <button
                  onClick={async () => {
                    try {
                      await axios.delete(`${API}/orders/${order.id}/ddt-attachment`);
                      setDdtAttachment(null);
                      toast.success("Allegato rimosso");
                    } catch { toast.error("Errore rimozione allegato"); }
                  }}
                  className="text-xs text-red-600 hover:underline"
                  data-testid="warehouse-ddt-attachment-remove"
                >
                  Rimuovi
                </button>
              </>
            ) : (
              <label className={`inline-flex items-center gap-1.5 text-xs border border-dashed border-stone-400 text-stone-600 rounded-full px-3 py-1.5 cursor-pointer hover:border-[#8A1C2A] hover:text-[#8A1C2A] ${uploadingDdt ? "opacity-50 pointer-events-none" : ""}`}>
                {uploadingDdt ? "Caricamento…" : "📎 Allega PDF del DDT originale"}
                <input
                  type="file"
                  accept="application/pdf,image/*"
                  className="hidden"
                  data-testid="warehouse-ddt-attachment-input"
                  onChange={async (e) => {
                    const f = e.target.files?.[0];
                    if (!f) return;
                    setUploadingDdt(true);
                    try {
                      const fd = new FormData();
                      fd.append("file", f);
                      const r = await axios.post(`${API}/orders/${order.id}/ddt-attachment`, fd, { headers: { "Content-Type": "multipart/form-data" } });
                      setDdtAttachment(r.data);
                      toast.success("DDT allegato all'ordine");
                    } catch (err) {
                      toast.error(err.response?.data?.detail || "Errore caricamento allegato");
                    } finally { setUploadingDdt(false); e.target.value = ""; }
                  }}
                />
              </label>
            )}
            <span className="text-[11px] text-stone-400">Visibile anche dal documento definitivo</span>
          </div>
        </div>
        </MobileCollapse>

        {/* Catalog search */}
        <MobileCollapse title="➕ Aggiungi articolo dal catalogo" testid="wh-collapse-catalog">
        <Card className="bg-cyan-50 border-cyan-200 p-3">
          <div className="flex items-center gap-2 mb-2">
            <Search className="w-4 h-4 text-cyan-700" />
            <p className="text-sm font-semibold text-cyan-800">Aggiungi articolo dal catalogo</p>
          </div>
          <div className="flex flex-wrap gap-2 items-start">
            <div className="flex-1 min-w-[200px] relative">
              <Input
                placeholder="Cerca per codice o descrizione (min. 2 caratteri)..."
                value={catalogSearch}
                onChange={e => setCatalogSearch(e.target.value)}
                className="bg-white border-cyan-300"
                data-testid="catalog-search-input"
              />
              {catalogResults.length > 0 && (
                <div className="absolute top-full left-0 right-0 mt-1 bg-white border border-stone-200 rounded-md shadow-lg max-h-60 overflow-y-auto z-20">
                  {catalogResults.map(p => (
                    <button
                      key={p.id}
                      type="button"
                      onClick={() => addFromCatalog(p)}
                      className="w-full text-left p-2 hover:bg-cyan-50 border-b border-stone-100 text-sm flex justify-between items-center"
                      data-testid={`catalog-result-${p.code}`}
                    >
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-1.5 flex-wrap">
                          <p className="font-mono text-xs text-stone-500">{p.code}</p>
                          {p.in_listino === false && (
                            <span className="bg-amber-100 text-amber-800 text-[9px] font-semibold px-1.5 py-0 rounded">Fuori Listino</span>
                          )}
                        </div>
                        <p className="font-medium truncate">{p.description}</p>
                      </div>
                      <span className="text-xs text-stone-500 ml-2">{p.unit}</span>
                    </button>
                  ))}
                </div>
              )}
            </div>
            <Button variant="outline" onClick={() => setShowExtraProductModal(true)} className="border-amber-500 text-amber-700 hover:bg-amber-50" data-testid="create-extra-product-btn" title="Crea un articolo non in listino — disponibile subito per l'ordine">
              <Plus className="w-3 h-3 mr-1" /> Fuori listino
            </Button>
          </div>
        </Card>
        </MobileCollapse>

        {/* Lettura DDT da PDF con AI — fallback, chiuso di default */}
        <div data-testid="wh-ai-scanner-section">
          <button
            type="button"
            onClick={() => setShowAiScanner((v) => !v)}
            className="w-full flex items-center justify-between text-left text-xs font-semibold text-stone-600 bg-stone-50 border border-stone-200 rounded-lg px-3 py-2 hover:bg-stone-100"
            data-testid="wh-toggle-ai-scanner"
          >
            <span className="flex items-center gap-2"><ScanLine className="w-3.5 h-3.5" /> Leggi DDT da PDF con AI <span className="font-normal text-stone-400">(solo se il DDT non è ancora arrivato da Etiflor)</span></span>
            <ChevronDown className={`w-4 h-4 transition-transform ${showAiScanner ? "rotate-180" : ""}`} />
          </button>
          {showAiScanner && (
        <Card className="mt-2 bg-gradient-to-r from-[#8A1C2A]/5 to-[#C5A059]/10 border-[#C5A059]/40 p-3">
          <div className="flex items-center justify-between gap-3 flex-wrap">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-[#8A1C2A] text-white flex items-center justify-center">
                <ScanLine className="w-5 h-5" />
              </div>
              <div>
                <p className="text-sm font-semibold text-stone-900">Leggi DDT da PDF con AI</p>
                <p className="text-xs text-stone-600">Carica il PDF del DDT: articoli, pesi, prezzi e sconti vengono compilati automaticamente.</p>
              </div>
            </div>
            <Button
              onClick={() => setShowDDTScanner(true)}
              className="bg-[#8A1C2A] hover:bg-[#6B1420] text-white"
              data-testid="warehouse-open-ddt-scanner"
            >
              <FileText className="w-4 h-4 mr-2" /> Carica PDF DDT
            </Button>
          </div>
        </Card>
          )}
        </div>

        <Card className="bg-white border-stone-200 overflow-hidden">
          <div className="hidden md:block overflow-x-auto">
          <table className="w-full text-sm min-w-[700px]">
            <thead className="bg-[#8A1C2A] text-white">
              <tr>
                <th className="text-left p-2 text-xs">Codice</th>
                <th className="text-left p-2 text-xs">Descrizione</th>
                <th className="text-right p-2 text-xs">Ord.</th>
                <th className="text-right p-2 text-xs w-28">Quantità reale</th>
                <th className="text-center p-2 text-xs w-20">U.M.</th>
                <th className="text-center p-2 text-xs w-32">Disponibilità</th>
                <th className="text-right p-2 text-xs w-24">€/UM</th>
                <th className="text-right p-2 text-xs w-20">Sconto %</th>
                <th className="text-right p-2 text-xs w-20">IVA %</th>
                <th className="text-right p-2 text-xs">Totale</th>
                <th className="text-left p-2 text-xs">Note</th>
              </tr>
            </thead>
            <tbody>
              {items.map((it, idx) => (
                <tr key={idx} className={`border-b border-stone-100 ${it.is_free ? "bg-amber-50" : ""}`} data-testid={`warehouse-item-${it.code}`}>
                  <td className="p-2">
                    {it.is_free ? (
                      <Input value={it.code || ""} onChange={e => updateItem(idx, "code", e.target.value)} placeholder="Assegna codice" className="h-7 text-xs font-mono bg-white border-amber-300" />
                    ) : (
                      <span className="font-mono text-xs">{it.code}</span>
                    )}
                  </td>
                  <td className="p-2">
                    {it.is_free ? (
                      <Input value={it.description || ""} onChange={e => updateItem(idx, "description", e.target.value)} placeholder="Descrizione articolo libero" className="h-7 text-xs bg-white border-amber-300" />
                    ) : (
                      <>
                        {it.description}
                        {it.is_free && <Badge className="ml-1 bg-amber-100 text-amber-700 text-[10px]">Libero</Badge>}
                      </>
                    )}
                  </td>
                  <td className="p-2 text-right text-stone-500">{it.quantity} {it.original_unit || it.unit}</td>
                  <td className="p-2">
                    <NumberInput
                      step="0.01" min="0"
                      value={it.actual_quantity}
                      onChange={e => updateItem(idx, "actual_quantity", parseFloat(e.target.value) || 0)}
                      className="h-8 text-right bg-white border-stone-300"
                      disabled={it.availability_status === "missing"}
                      data-testid={`warehouse-qty-${it.code}`}
                    />
                  </td>
                  <td className="p-2">
                    <Select value={it.unit} onValueChange={v => updateItem(idx, "unit", v)}>
                      <SelectTrigger className="h-8 text-xs bg-white border-stone-300" data-testid={`warehouse-unit-${it.code}`}><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="Kg">Kg</SelectItem>
                        <SelectItem value="Pz">Pz</SelectItem>
                        <SelectItem value="CT">CT</SelectItem>
                      </SelectContent>
                    </Select>
                  </td>
                  <td className="p-2">
                    <Select value={it.availability_status} onValueChange={v => updateItem(idx, "availability_status", v)}>
                      <SelectTrigger className={`h-8 text-xs ${AV_COLOR[it.availability_status]}`}><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="delivered">✅ Spedito</SelectItem>
                        <SelectItem value="partial">⚠️ Parziale</SelectItem>
                        <SelectItem value="pending">⏳ In attesa</SelectItem>
                        <SelectItem value="missing">❌ Mancante</SelectItem>
                      </SelectContent>
                    </Select>
                  </td>
                  <td className="p-2 text-right">
                    <NumberInput
                      step="0.01" min="0"
                      value={it.unit_price}
                      onChange={e => updateItem(idx, "unit_price", parseFloat(e.target.value) || 0)}
                      className={`h-7 text-right bg-white w-20 ${it.is_free ? "border-amber-300" : "border-stone-300"}`}
                      data-testid={`warehouse-price-${it.code}`}
                    />
                  </td>
                  <td className="p-2">
                    <NumberInput step="0.5" min="0" max="100" value={it.discount_percent ?? 0} onChange={e => updateItem(idx, "discount_percent", parseFloat(e.target.value) || 0)} className="h-7 text-right bg-white border-stone-300 w-16" data-testid={`warehouse-discount-${it.code}`} />
                  </td>
                  <td className="p-2">
                    <NumberInput value={it.vat_rate} onChange={e => updateItem(idx, "vat_rate", parseFloat(e.target.value) || 0)} className="h-7 text-right bg-white border-stone-300 w-16" />
                  </td>
                  <td className="p-2 text-right font-semibold text-[#8A1C2A]">
                    {fm((it.availability_status === "missing" ? 0 : lineNet(it)))}
                  </td>
                  <td className="p-2">
                    <Input
                      value={it.warehouse_note}
                      onChange={e => updateItem(idx, "warehouse_note", e.target.value)}
                      placeholder="es. tagliato a misura"
                      className="h-8 text-xs bg-white border-stone-300"
                    />
                  </td>
                </tr>
              ))}
              {newItems.map((ni, idx) => (
                <tr key={`new-${idx}`} className={`border-b border-stone-100 ${ni.product_id ? "bg-cyan-50" : "bg-amber-50"}`} data-testid={`warehouse-new-item-${idx}`}>
                  <td className="p-2"><Input value={ni.code} onChange={e => updateNewItem(idx, "code", e.target.value)} placeholder="Codice" className="h-7 text-xs font-mono bg-white" /></td>
                  <td className="p-2"><Input value={ni.description} onChange={e => updateNewItem(idx, "description", e.target.value)} placeholder="Descrizione" className="h-7 text-xs bg-white" /></td>
                  <td className="p-2 text-right text-stone-400 text-xs italic">{ni.product_id ? "— catalogo —" : "— libero —"}</td>
                  <td className="p-2"><NumberInput value={ni.quantity} onChange={e => updateNewItem(idx, "quantity", parseFloat(e.target.value) || 0)} className="h-8 text-right bg-white border-stone-300" /></td>
                  <td className="p-2">
                    <Select value={ni.unit} onValueChange={v => updateNewItem(idx, "unit", v)}>
                      <SelectTrigger className="h-8 text-xs"><SelectValue /></SelectTrigger>
                      <SelectContent><SelectItem value="Kg">Kg</SelectItem><SelectItem value="Pz">Pz</SelectItem><SelectItem value="CT">CT</SelectItem></SelectContent>
                    </Select>
                  </td>
                  <td className="p-2"><NumberInput value={ni.unit_price} onChange={e => updateNewItem(idx, "unit_price", parseFloat(e.target.value) || 0)} className="h-7 text-right bg-white border-stone-300 w-20" /></td>
                  <td className="p-2 text-center text-xs text-stone-400">—</td>
                  <td className="p-2"><NumberInput value={ni.vat_rate} onChange={e => updateNewItem(idx, "vat_rate", parseFloat(e.target.value) || 0)} className="h-7 text-right bg-white border-stone-300 w-16" /></td>
                  <td className="p-2"><NumberInput step="1" value={ni.package_count ?? 1} onChange={e => updateNewItem(idx, "package_count", parseInt(e.target.value) || 0)} className="h-7 text-right bg-white border-stone-300 w-14" /></td>
                  <td className="p-2 text-right font-semibold text-[#8A1C2A]">{fm((ni.quantity * ni.unit_price))}</td>
                  <td className="p-2"><Button size="icon" variant="ghost" className="h-7 w-7" onClick={() => setNewItems(newItems.filter((_, i) => i !== idx))}><X className="w-3 h-3 text-red-500" /></Button></td>
                </tr>
              ))}
            </tbody>
            <tfoot className="bg-stone-50">
              <tr>
                <td colSpan="11" className="p-2 text-right">
                  <Button size="sm" variant="outline" onClick={addEmptyNewItem} className="border-amber-300 text-amber-700 mr-2" data-testid="warehouse-add-free-item-btn">
                    <Plus className="w-3 h-3 mr-1" /> Articolo libero
                  </Button>
                </td>
              </tr>
              <tr className="border-t-2 border-[#8A1C2A] bg-[#FAF8F5]">
                <td colSpan="3" className="p-3 font-bold text-[#8A1C2A]">TOTALI</td>
                <td className="p-3 text-right font-bold text-[#8A1C2A]" data-testid="warehouse-total-kg">{totalKg.toFixed(2)} Kg</td>
                <td className="p-3 text-center text-xs text-stone-500" colSpan="4">—</td>
                <td className="p-3 text-right font-bold text-[#8A1C2A]" data-testid="warehouse-total-packages">{totalPackages} colli</td>
                <td></td>
                <td></td>
              </tr>
              <tr className="border-t-2 border-stone-200">
                <td colSpan="9" className="p-3 text-right font-semibold">Imponibile</td>
                <td className="p-3 text-right font-semibold">{fm(subtotal)}</td>
                <td></td>
              </tr>
              <tr>
                <td colSpan="9" className="px-3 py-1 text-right text-xs">IVA {isForeign ? "(estero 0%)" : ""}</td>
                <td className="px-3 py-1 text-right text-xs">{fm(vatTotal)}</td>
                <td></td>
              </tr>
              <tr className="border-t border-stone-200">
                <td colSpan="9" className="p-3 text-right font-semibold">Totale finale</td>
                <td className="p-3 text-right text-lg font-bold text-[#8A1C2A]">{fm(grandTotal)}</td>
                <td></td>
              </tr>
              <tr>
                <td colSpan="9" className="px-3 pb-3 text-right text-xs text-stone-500">Provvigione agente ({((order.commission_rate || 0.09) * 100).toFixed(1)}%)</td>
                <td className="px-3 pb-3 text-right text-sm font-semibold text-[#C5A059]">{fm(finalCommission)}</td>
                <td></td>
              </tr>
            </tfoot>
          </table>
          </div>

          {/* Mobile card view for items */}
          <div className="md:hidden divide-y divide-stone-100" data-testid="wh-items-mobile">
            {items.map((it, idx) => (
              <div key={idx} className={`p-3 ${it.availability_status === "missing" ? "bg-red-50" : "bg-white"}`}>
                <div className="flex items-start justify-between gap-2 mb-2">
                  <div className="flex-1 min-w-0">
                    <p className="text-xs font-mono text-stone-500">{it.code}</p>
                    <p className="text-sm font-medium leading-tight">{it.description}</p>
                  </div>
                  <Badge className={availColors[it.availability_status]} data-testid={`wh-mobile-status-${it.code}`}>{availLabels[it.availability_status]}</Badge>
                </div>
                <div className="grid grid-cols-2 gap-2 mt-2 text-xs">
                  <div>
                    <Label className="text-[10px] text-stone-500">Ordinato</Label>
                    <div className="font-mono h-8 flex items-center">{it.quantity} {it.original_unit || it.unit}</div>
                  </div>
                  <div>
                    <Label className="text-[10px] text-stone-500">Peso reale</Label>
                    <NumberInput
                      step="0.01" min="0"
                      value={it.actual_quantity}
                      onChange={e => updateItem(idx, "actual_quantity", parseFloat(e.target.value) || 0)}
                      className="h-8 bg-white border-stone-300"
                      disabled={it.availability_status === "missing"}
                      data-testid={`wh-mobile-actual-${it.code}`}
                    />
                  </div>
                  <div>
                    <Label className="text-[10px] text-stone-500">U.M.</Label>
                    <Select value={it.unit} onValueChange={v => updateItem(idx, "unit", v)}>
                      <SelectTrigger className="h-8 bg-white border-stone-300 text-xs" data-testid={`wh-mobile-unit-${it.code}`}><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="Kg">Kg</SelectItem>
                        <SelectItem value="Pz">Pz</SelectItem>
                        <SelectItem value="CT">CT</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label className="text-[10px] text-stone-500">€/U.M.</Label>
                    <NumberInput
                      step="0.01" min="0"
                      value={it.unit_price}
                      onChange={e => updateItem(idx, "unit_price", parseFloat(e.target.value) || 0)}
                      className="h-8 bg-white border-stone-300"
                      data-testid={`wh-mobile-price-${it.code}`}
                    />
                  </div>
                </div>
                <div className="flex items-center justify-between mt-2">
                  <span className="text-[10px] text-stone-500">Totale riga</span>
                  <span className="font-bold text-[#8A1C2A]" data-testid={`wh-mobile-total-${it.code}`}>{fm((it.availability_status === "missing" ? 0 : lineNet(it)))}</span>
                </div>
                <details className="mt-2 border border-stone-200 rounded-lg bg-stone-50">
                  <summary className="text-xs text-stone-600 font-medium px-2.5 py-2 cursor-pointer select-none" data-testid={`wh-mobile-more-${it.code}`}>⚙️ Altre opzioni (sconto, IVA, colli, stato, nota)</summary>
                  <div className="p-2.5 pt-1 space-y-2">
                    <div className="grid grid-cols-3 gap-2 text-xs">
                      <div>
                        <Label className="text-[10px] text-stone-500">Sconto %</Label>
                        <NumberInput
                          step="0.5" min="0" max="100"
                          value={it.discount_percent ?? 0}
                          onChange={e => updateItem(idx, "discount_percent", parseFloat(e.target.value) || 0)}
                          className="h-8 bg-white border-stone-300"
                          data-testid={`wh-mobile-discount-${it.code}`}
                        />
                      </div>
                      <div>
                        <Label className="text-[10px] text-stone-500">IVA</Label>
                        <NumberInput
                          step="0.01" min="0" max="1"
                          value={it.vat_rate}
                          onChange={e => updateItem(idx, "vat_rate", parseFloat(e.target.value) || 0)}
                          className="h-8 bg-white border-stone-300"
                        />
                      </div>
                      <div>
                        <Label className="text-[10px] text-stone-500">Colli</Label>
                        <NumberInput
                          step="1" min="0"
                          value={it.package_count ?? 1}
                          onChange={e => updateItem(idx, "package_count", parseInt(e.target.value) || 0)}
                          className="h-8 bg-white border-stone-300"
                          disabled={it.availability_status === "missing"}
                          data-testid={`wh-mobile-pkg-${it.code}`}
                        />
                      </div>
                    </div>
                    <div>
                      <Label className="text-[10px] text-stone-500">Stato</Label>
                      <Select value={it.availability_status} onValueChange={v => updateItem(idx, "availability_status", v)}>
                        <SelectTrigger className="h-8 bg-white border-stone-300"><SelectValue /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="delivered">Consegnato</SelectItem>
                          <SelectItem value="partial">Parziale</SelectItem>
                          <SelectItem value="missing">Mancante</SelectItem>
                          <SelectItem value="pending">In attesa</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <Input
                      placeholder="Nota..."
                      value={it.warehouse_note}
                      onChange={e => updateItem(idx, "warehouse_note", e.target.value)}
                      className="h-8 text-xs bg-white border-stone-300"
                    />
                  </div>
                </details>
              </div>
            ))}
            {newItems.length > 0 && (
              <div className="p-3 bg-amber-50 border-t border-amber-200">
                <p className="text-xs text-amber-800 font-semibold mb-2">{newItems.length} nuovi articoli aggiunti</p>
              </div>
            )}
            <div className="p-3 bg-stone-50 border-t-2 border-[#8A1C2A]">
              <div className="grid grid-cols-2 gap-2 text-sm">
                <div className="text-stone-600">Totale Kg</div>
                <div className="text-right font-bold text-[#8A1C2A]" data-testid="warehouse-total-kg-mobile">{totalKg.toFixed(2)} Kg</div>
                <div className="text-stone-600">Totale colli</div>
                <div className="text-right font-bold text-[#8A1C2A]" data-testid="warehouse-total-packages-mobile">{totalPackages}</div>
                <div className="text-stone-600">Imponibile</div>
                <div className="text-right font-semibold">{fm(subtotal)}</div>
                <div className="text-stone-600 text-xs">IVA {isForeign ? "(estero 0%)" : ""}</div>
                <div className="text-right text-xs">{fm(vatTotal)}</div>
                <div className="text-stone-900 font-semibold pt-1 border-t border-stone-200 mt-1">Totale finale</div>
                <div className="text-right font-bold text-[#8A1C2A] pt-1 border-t border-stone-200 mt-1">{fm(grandTotal)}</div>
              </div>
            </div>
          </div>
        </Card>

        {/* Extra costs (shipping, handling, etc.) */}
        <MobileCollapse title="💰 Costi extra (trasporto, lavorazione...)" testid="wh-collapse-extra-costs">
        <Card className="bg-white border-stone-200 p-4">
          <div className="flex items-center justify-between mb-3">
            <h4 className="font-semibold text-stone-900">💰 Costi extra (trasporto, lavorazione, altro)</h4>
            <Button size="sm" variant="outline" onClick={addEmptyExtraCost} className="border-stone-300" data-testid="warehouse-add-extra-btn">
              <Plus className="w-3 h-3 mr-1" /> Aggiungi costo
            </Button>
          </div>
          {extraCosts.length === 0 ? (
            <p className="text-xs text-stone-500 italic">Nessun costo extra</p>
          ) : (
            <div className="space-y-2">
              {extraCosts.map((ec, idx) => (
                <div key={idx} className="grid grid-cols-12 gap-2 items-center" data-testid={`extra-cost-${idx}`}>
                  <Input value={ec.description} onChange={e => updateExtraCost(idx, "description", e.target.value)} placeholder="Es. Spese trasporto" className="col-span-6 h-9 bg-white border-stone-300" />
                  <Input type="number" step="0.01" value={ec.amount} onChange={e => updateExtraCost(idx, "amount", parseFloat(e.target.value) || 0)} placeholder="€" className="col-span-2 h-9 text-right bg-white border-stone-300" />
                  <Input type="number" step="0.01" value={ec.vat_rate} onChange={e => updateExtraCost(idx, "vat_rate", parseFloat(e.target.value) || 0)} placeholder="IVA" className="col-span-2 h-9 text-right bg-white border-stone-300" />
                  <span className="col-span-1 text-right text-sm font-semibold text-[#8A1C2A]">{fm(((ec.amount || 0) * (isForeign ? 1 : (1 + (ec.vat_rate || 0)))))}</span>
                  <Button size="icon" variant="ghost" className="col-span-1 h-8 w-8" onClick={() => setExtraCosts(extraCosts.filter((_, i) => i !== idx))}><X className="w-3 h-3 text-red-500" /></Button>
                </div>
              ))}
            </div>
          )}
        </Card>
        </MobileCollapse>

        <div>
          <Label className="text-xs">Note magazzino (visibili a admin e agente)</Label>
          <textarea
            rows={2}
            value={warehouseNotes}
            onChange={e => setWarehouseNotes(e.target.value)}
            placeholder="Es. Lotto 12345, alcune fette tagliate manualmente"
            className="w-full mt-1 px-3 py-2 text-sm bg-white border border-stone-300 rounded-md"
            data-testid="warehouse-notes-area"
          />
        </div>

        {/* Azioni documento (PDF, condivisione, etichette, chat) */}
        <div className="flex flex-wrap gap-2">
          <Button variant="outline" size="sm" onClick={() => previewPDF(false)} className="border-stone-300 h-9" data-testid="preview-pdf-btn">
            <Eye className="w-4 h-4 mr-1" /> Anteprima
          </Button>
          <Button variant="outline" size="sm" onClick={() => downloadPDF(false)} className="border-stone-300 h-9" data-testid="download-pdf-btn">
            <Download className="w-4 h-4 mr-1" /> PDF interno
          </Button>
          <Button variant="outline" size="sm" onClick={() => downloadPDF(true)} className="border-stone-300 h-9" data-testid="download-customer-pdf-btn">
            <Download className="w-4 h-4 mr-1" /> PDF cliente
          </Button>
          <Button variant="outline" size="sm" onClick={shareWhatsApp} className="border-green-400 text-green-700 hover:bg-green-50 h-9" data-testid="whatsapp-share-btn">
            <MessageCircle className="w-4 h-4 mr-1" /> WhatsApp
          </Button>
          <Button variant="outline" size="sm" onClick={() => setShowLabelsDialog(true)} className="border-[#C5A059] text-[#C5A059] hover:bg-[#FAF8F5] h-9" data-testid="open-labels-dialog-btn">
            <Package className="w-4 h-4 mr-1" /> Etichette
          </Button>
          <Button variant="outline" size="sm" onClick={() => { onOpenChat?.(order.id); onClose(); }} className="border-[#8A1C2A] text-[#8A1C2A] hover:bg-[#8A1C2A]/10 h-9" data-testid="wh-order-chat-btn">
            <MessageCircle className="w-4 h-4 mr-1" /> Chat ordine
          </Button>
        </div>

        <DialogFooter className="flex-row flex-wrap gap-2 justify-end sticky bottom-0 bg-white border-t border-stone-200 pt-3 -mx-3 px-3 pb-1 sm:static sm:mx-0 sm:px-0 sm:pb-0 sm:border-0 sm:pt-0 z-10">
          <Button variant="outline" onClick={onClose} className="border-stone-300">Annulla</Button>
          <Button onClick={save} disabled={saving} className="bg-[#8A1C2A] hover:bg-[#6B1420] text-white flex-1 sm:flex-none" data-testid="warehouse-save-btn">
            <Save className="w-4 h-4 mr-1" /> {saving ? "Salvataggio..." : "Salva"}
          </Button>
        </DialogFooter>
      </DialogContent>
      <LabelsPrintDialog open={showLabelsDialog} onClose={() => setShowLabelsDialog(false)} order={order} items={items.concat(newItems.filter(ni => ni.code || ni.product_id))} />
      <ExtraProductModal
        open={showExtraProductModal}
        onClose={() => setShowExtraProductModal(false)}
        onCreated={(prod) => {
          // Auto-add the just-created extra product to the order
          setNewItems(prev => [...prev, {
            product_id: prod.id,
            code: prod.code,
            description: prod.description,
            quantity: 1,
            unit: prod.unit || "Kg",
            unit_price: 0,
            line_total: 0,
            is_free: false,
            vat_rate: prod.vat_rate ?? 0.10,
          }]);
          setShowExtraProductModal(false);
          toast.success(`Articolo fuori listino "${prod.code}" creato e aggiunto all'ordine`);
        }}
      />
      <DDTCameraScanner
        open={showDDTScanner}
        onClose={() => setShowDDTScanner(false)}
        orderId={order.id}
        orderItems={items.map(it => ({ code: it.code, description: it.description, unit: it.unit }))}
        onAttached={(att) => setDdtAttachment(att)}
        onApply={(payload) => {
          // payload: { mode: 'merge' | 'replace', patches?, lines?, document_number?, document_date? }
          // Numero DDT letto dall'AI → precompila il Rif. DDT se vuoto
          if (payload.document_number && !documentReference) {
            setDocumentReference(payload.document_number);
            toast.info(`Rif. DDT compilato automaticamente: ${payload.document_number}`);
          }
          // Data DDT letta dall'AI → sincronizzata sulla data di spedizione (e quindi sul documento)
          if (payload.document_date) {
            const raw = String(payload.document_date).trim();
            const m = raw.match(/^(\d{1,2})[/\-.](\d{1,2})[/\-.](\d{2,4})$/);
            const iso = m
              ? `${m[3].length === 2 ? "20" + m[3] : m[3]}-${m[2].padStart(2, "0")}-${m[1].padStart(2, "0")}`
              : (/^\d{4}-\d{2}-\d{2}$/.test(raw) ? raw : null);
            if (iso) {
              setShippingDate(iso);
              toast.info(`Data DDT/spedizione compilata: ${new Date(iso + "T00:00:00").toLocaleDateString("it-IT")}`);
            }
          }
          if (payload.mode === "replace" && payload.lines) {
            // Build a fresh items array. For each scanned line:
            //  - if matched to an existing order item, keep id but update code/desc/qty
            //  - if manual (no match), create a free line (is_free)
            const rebuilt = payload.lines.map((line) => {
              if (line.order_item_index != null && items[line.order_item_index]) {
                const orig = items[line.order_item_index];
                return {
                  ...orig,
                  code: line.code || orig.code,
                  description: line.description || orig.description,
                  actual_quantity: line.quantity,
                  unit: line.unit || orig.unit || "Kg",
                  unit_price: line.price != null ? line.price : orig.unit_price,
                  discount_percent: line.discount_percent ?? orig.discount_percent ?? 0,
                  availability_status: "delivered",
                };
              }
              // free row for codes not in the order
              return {
                product_id: null,
                code: line.code,
                description: line.description,
                quantity: line.quantity,
                actual_quantity: line.quantity,
                unit: line.unit || "Kg",
                unit_price: line.price || 0,
                discount_percent: line.discount_percent || 0,
                line_total: 0,
                is_free: true,
                availability_status: "delivered",
                vat_rate: 0.10,
              };
            });
            setItems(rebuilt);
            setItemsReplaced(true);
            return;
          }
          // merge mode (default)
          const patches = payload.patches || {};
          setItems(prev => prev.map((it, idx) => {
            const p = patches[idx];
            if (!p) return it;
            const updated = { ...it };
            if (typeof p.actual_quantity === "number" && p.actual_quantity > 0) {
              updated.actual_quantity = p.actual_quantity;
              updated.availability_status = "delivered";
            }
            if (typeof p.price === "number" && p.price > 0) updated.unit_price = p.price;
            if (typeof p.discount_percent === "number") updated.discount_percent = p.discount_percent;
            if (typeof p.unit === "string" && p.unit) updated.unit = p.unit;
            if (p.action === "substitute" && it.is_free) {
              if (p.actual_code) updated.code = p.actual_code;
              if (p.actual_description) updated.description = p.actual_description;
            }
            return updated;
          }));
        }}
      />
    </Dialog>
  );
};
// ============ Labels Print Dialog moved to /components/LabelsPrintDialog.js ============




// ============ EXTRA PRODUCT (Fuori Listino) Modal — Magazzino crea e usa subito ============
const ExtraProductModal = ({ open, onClose, onCreated }) => {
  const { t } = useLang();
  const [categories, setCategories] = useState([]);
  const [form, setForm] = useState({
    code: "", description: "", category_id: "", unit: "Kg", note: "",
  });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (open) {
      setForm({ code: "", description: "", category_id: "", unit: "Kg", note: "" });
      axios.get(`${API}/categories`).then(r => setCategories(r.data || [])).catch(() => {});
    }
  }, [open]);

  const submit = async () => {
    if (!form.code.trim() || !form.description.trim()) {
      toast.error("Codice e descrizione obbligatori");
      return;
    }
    setSaving(true);
    try {
      const payload = {
        code: form.code.trim().toUpperCase(),
        description: form.description.trim(),
        unit: form.unit,
        note: form.note,
      };
      if (form.category_id) payload.category_id = form.category_id;
      const r = await axios.post(`${API}/products/extra`, payload);
      onCreated?.(r.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Errore creazione articolo");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-xl bg-white" data-testid="extra-product-modal">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Plus className="w-5 h-5 text-amber-600" /> Crea articolo Fuori Listino
          </DialogTitle>
        </DialogHeader>
        <p className="text-sm text-stone-600">
          Crea immediatamente un articolo non presente nel listino cliente (es. <em>Testa di filetto</em>). Disponibile subito per evadere l'ordine, ma <strong>non</strong> verrà mostrato nel catalogo cliente / agente o nel PDF listino.
        </p>
        <div className="grid grid-cols-2 gap-3">
          <div>
            <Label>Codice *</Label>
            <Input value={form.code} onChange={e => setForm({ ...form, code: e.target.value })} className="bg-white border-stone-300 font-mono uppercase" placeholder="es. TF-001" data-testid="extra-code" />
          </div>
          <div>
            <Label>{t.pCust_filterCategory}</Label>
            <Select value={form.category_id} onValueChange={v => setForm({ ...form, category_id: v })}>
              <SelectTrigger className="bg-white border-stone-300" data-testid="extra-category"><SelectValue placeholder="(auto)" /></SelectTrigger>
              <SelectContent>{categories.map(c => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div className="col-span-2">
            <Label>Descrizione *</Label>
            <Input value={form.description} onChange={e => setForm({ ...form, description: e.target.value })} className="bg-white border-stone-300" placeholder="es. Testa di filetto" data-testid="extra-description" />
          </div>
          <div>
            <Label>U.M.</Label>
            <Select value={form.unit} onValueChange={v => setForm({ ...form, unit: v })}>
              <SelectTrigger className="bg-white border-stone-300"><SelectValue /></SelectTrigger>
              <SelectContent><SelectItem value="Kg">Kg</SelectItem><SelectItem value="Pz">Pz</SelectItem><SelectItem value="CT">CT</SelectItem></SelectContent>
            </Select>
          </div>
          <div className="col-span-2"><Label>Note</Label><Input value={form.note} onChange={e => setForm({ ...form, note: e.target.value })} className="bg-white border-stone-300" /></div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} className="border-stone-300">Annulla</Button>
          <Button onClick={submit} disabled={saving} className="bg-amber-600 hover:bg-amber-700 text-white" data-testid="submit-extra-btn">
            {saving ? "Creazione..." : "Crea e usa subito"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

const OrdersToPrepare = () => {
  const { t } = useLang();
  const fm = useFmt();
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [filter, setFilter] = useState("active");
  const [selected, setSelected] = useState(null);
  const [chatOrderId, setChatOrderId] = useState(null);
  const [selectedIds, setSelectedIds] = useState(() => new Set());
  const [printing, setPrinting] = useState(false);
  const [creditMap, setCreditMap] = useState({});
  const [ddtCands, setDdtCands] = useState({});
  const [deliveryMap, setDeliveryMap] = useState({});
  const location = useLocation();

  useEffect(() => {
    axios.get(`${API}/customers-credit-summary`).then((r) => setCreditMap(r.data || {})).catch(() => {});
  }, []);
  const loadDdtCands = () => axios.get(`${API}/etiflor-ddt/candidates`).then((r) => setDdtCands(r.data || {})).catch(() => {});
  const loadDelivery = () => axios.get(`${API}/logistics/orders-delivery`).then((r) => setDeliveryMap(r.data || {})).catch(() => {});

  const DdtBadge = ({ o, mobile }) => {
    const sfx = mobile ? "-mobile" : "";
    if (o.etiflor_ddt_numero) {
      return <span className="ml-1.5 inline-flex items-center bg-emerald-600 text-white text-[9px] font-bold px-1.5 py-0.5 rounded-full align-middle" title="Collegato a DDT Etiflor" data-testid={`wh-etiflor-linked${sfx}-${o.order_number}`}>📄 DDT {o.etiflor_ddt_numero}</span>;
    }
    const n = (ddtCands[o.id] || []).length;
    if (!n) return null;
    return <span className="ml-1.5 inline-flex items-center bg-emerald-100 text-emerald-800 border border-emerald-400 text-[9px] font-bold px-1.5 py-0.5 rounded-full align-middle animate-pulse" title="DDT sincronizzato da Etiflor pronto da collegare" data-testid={`wh-etiflor-cand${sfx}-${o.order_number}`}>📄 DDT ETIFLOR {n > 1 ? `(${n})` : ""}</span>;
  };

  const CreditCell = ({ o }) => {
    const ci = creditMap[o.customer_id];
    if (!ci || ((ci.overdue_amount || 0) <= 0 && (ci.open_amount || 0) <= 0)) return null;
    return (
      <div className="flex flex-wrap gap-1 mt-1 text-[10px] font-bold" data-testid={`wh-credit-${o.order_number}`}>
        {ci.overdue_amount > 0 && <span className="bg-red-100 text-red-700 border border-red-200 px-1.5 py-0.5 rounded" data-testid={`wh-credit-overdue-${o.order_number}`}>SCADUTO {fm(ci.overdue_amount)}</span>}
        {ci.open_amount > 0 && <span className="bg-stone-100 text-stone-700 border border-stone-200 px-1.5 py-0.5 rounded" data-testid={`wh-credit-open-${o.order_number}`}>APERTO {fm(ci.open_amount)}</span>}
      </div>
    );
  };

  const toggleSelectOne = (id) => {
    const next = new Set(selectedIds);
    if (next.has(id)) next.delete(id); else next.add(id);
    setSelectedIds(next);
  };
  const toggleSelectAll = (list) => {
    const allIn = list.length > 0 && list.every((o) => selectedIds.has(o.id));
    if (allIn) setSelectedIds(new Set());
    else setSelectedIds(new Set(list.map((o) => o.id)));
  };
  const printBatchSummary = async () => {
    if (selectedIds.size === 0) return;
    setPrinting(true);
    try {
      const ok = await openAuthenticatedPdfPost(`/orders/batch-items-summary-pdf`,
        { order_ids: Array.from(selectedIds) }, { filename: "Riepiloghi-ordini.pdf" });
      if (ok) toast.success(`${selectedIds.size} riepiloghi pronti per la stampa`);
    } finally {
      setPrinting(false);
    }
  };
  const printSingleSummary = (orderId, e) => {
    e?.stopPropagation?.();
    openAuthenticatedPdfPost(`/orders/${orderId}/items-summary-label-pdf`, null, { filename: "Riepilogo-ordine.pdf" });
  };
  const printCustomerNameLabel = (orderId, e) => {
    e?.stopPropagation?.();
    openAuthenticatedPdfPost(`/orders/${orderId}/customer-name-label-pdf?copies=1`, null, { filename: "Etichetta-cliente.pdf" });
  };

  // Ordine "non letto": nuovo ordine mai aperto dal magazzino
  const isUnread = (o) => !o.warehouse_viewed_at && o.status === "pending";
  const [ddtUploadOpen, setDdtUploadOpen] = useState(false);
  const hasCR = (o) => (o.change_requests || []).some((r) => r.status === "pending");
  const openOrder = (o) => {
    setSelected(o);
    if (isUnread(o)) {
      axios.post(`${API}/orders/${o.id}/mark-viewed`).catch(() => {});
      setOrders((prev) => prev.map((x) => (x.id === o.id ? { ...x, warehouse_viewed_at: new Date().toISOString() } : x)));
    }
  };

  // Auto-open order detail from ?openOrderId=... / ?openOrderNumber=... (deep-link from chat)
  useEffect(() => {
    if (!orders.length) return;
    const params = new URLSearchParams(location.search);
    const openId = params.get("openOrderId");
    const openNum = params.get("openOrderNumber");
    if (!openId && !openNum) return;
    const found = orders.find(o => o.id === openId || o.order_number === openNum || o.order_number === openId);
    if (found) setSelected(found);
  }, [orders, location.search]);

  const load = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (search) params.append("search", search);
      const r = await axios.get(`${API}/orders?${params}`);
      setOrders(r.data || []);
      loadDdtCands();
      loadDelivery();
    } catch { toast.error(t.pAgent_loadError); }
    finally { setLoading(false); }
  };
  useEffect(() => { const t = setTimeout(load, 300); return () => clearTimeout(t); /* eslint-disable-next-line */ }, [search]);

  // "Da preparare" = sempre tutti visibili in cima, indipendentemente dal filtro
  const PREP_STATUSES = ["pending", "confirmed", "postponed", "pending_agent", "in_preparation"];
  const prepOrders = orders.filter((o) => PREP_STATUSES.includes(o.status));
  const doneOrders = (() => {
    const rest = orders.filter((o) => !PREP_STATUSES.includes(o.status));
    if (filter === "active") return rest.filter((o) => !["completed", "cancelled", "delivered"].includes(o.status));
    if (filter === "all") return rest;
    return rest.filter((o) => o.status === filter);
  })();

  const groupByDate = (list) => {
    const groups = new Map();
    for (const o of list) {
      const d = (o.created_at || "").slice(0, 10) || "—";
      if (!groups.has(d)) groups.set(d, []);
      groups.get(d).push(o);
    }
    return Array.from(groups.entries()).sort((a, b) => b[0].localeCompare(a[0]));
  };

  const renderDesktopTable = (list, secKey) => (
                <div className="hidden md:block overflow-x-auto">
                <table className="w-full text-sm min-w-[900px]">
                  <thead className="bg-stone-50 border-b border-stone-200">
                    <tr>
                      <th className="p-3 w-8 text-center">
                        <input
                          type="checkbox"
                          checked={list.length > 0 && list.every((o) => selectedIds.has(o.id))}
                          onChange={() => toggleSelectAll(list)}
                          className="w-4 h-4 accent-stone-900 cursor-pointer"
                          data-testid={`wh-select-all-${secKey}`}
                        />
                      </th>
                      <th className="text-left p-3 text-xs font-semibold text-stone-600">N° Ordine</th>
                      <th className="text-left p-3 text-xs font-semibold text-stone-600">Data</th>
                      <th className="text-left p-3 text-xs font-semibold text-stone-600">Cliente</th>
                      <th className="text-left p-3 text-xs font-semibold text-stone-600">Agente</th>
                      <th className="text-center p-3 text-xs font-semibold text-stone-600">Art.</th>
                      <th className="text-right p-3 text-xs font-semibold text-stone-600">Totale stimato</th>
                      <th className="text-right p-3 text-xs font-semibold text-stone-600">Tot. reale</th>
                      <th className="text-center p-3 text-xs font-semibold text-stone-600">Spediz.</th>
                      <th className="text-left p-3 text-xs font-semibold text-stone-600">Consegna</th>
                      <th className="text-center p-3 text-xs font-semibold text-stone-600">Stato</th>
                      <th className="text-right p-3 text-xs font-semibold text-stone-600">Azioni</th>
                    </tr>
                  </thead>
                  <tbody>
                    {groupByDate(list).flatMap(([dateKey, rows]) => {
                      const label = dateKey === "—" ? "Senza data" : new Date(dateKey).toLocaleDateString("it-IT", { weekday: "long", day: "2-digit", month: "long", year: "numeric" });
                      return [
                        <tr key={`group-${secKey}-${dateKey}`} className="bg-stone-100 border-y-2 border-stone-300">
                          <td colSpan={12} className="p-2 text-xs font-bold uppercase tracking-wide text-stone-700" data-testid={`wh-date-group-${secKey}-${dateKey}`}>
                            📅 {label} <span className="text-stone-500 font-normal">· {rows.length} {rows.length === 1 ? "ordine" : "ordini"}</span>
                          </td>
                        </tr>,
                        ...rows.map((o) => {
                          const footNotes = o.customer?.special_requests;
                          const rowNotes = o.delivery_notes || footNotes || o.warehouse_notes || o.internal_notes;
                          return (
                            <React.Fragment key={o.id}>
                            <tr className={`${rowNotes ? "" : "border-b border-stone-100"} cursor-pointer warehouse-order-row ${isUnread(o) ? "bg-amber-50 hover:bg-amber-100" : "hover:bg-stone-50"}`} onClick={() => openOrder(o)} data-testid={`warehouse-order-${o.order_number}`} data-row-action="open-warehouse-editor">
                              <td className="p-3 text-center" onClick={(e) => e.stopPropagation()}>
                                <input
                                  type="checkbox"
                                  checked={selectedIds.has(o.id)}
                                  onChange={() => toggleSelectOne(o.id)}
                                  className="w-4 h-4 accent-stone-900 cursor-pointer"
                                  data-testid={`wh-select-${o.order_number}`}
                                />
                              </td>
                              <td className="p-3 font-mono font-semibold text-[#8A1C2A]">
                                {o.order_number}
                                {isUnread(o) && <span className="ml-1.5 inline-flex items-center bg-amber-400 text-stone-900 text-[9px] font-bold px-1.5 py-0.5 rounded-full animate-pulse align-middle" data-testid={`wh-unread-${o.order_number}`}>NUOVO</span>}
                                {hasCR(o) && <span className="ml-1.5 inline-flex items-center bg-purple-600 text-white text-[9px] font-bold px-1.5 py-0.5 rounded-full animate-pulse align-middle" data-testid={`wh-cr-${o.order_number}`}>🔁 MODIFICA</span>}
                                {o.advance_payment_required && <span className="ml-1.5 inline-flex items-center bg-red-600 text-white text-[9px] font-bold px-1.5 py-0.5 rounded-full align-middle" data-testid={`wh-advpay-${o.order_number}`}>💰 PAG. ANTICIPATO</span>}
                                <DdtBadge o={o} />
                              </td>
                              <td className="p-3 text-xs text-stone-500">{new Date(o.created_at).toLocaleTimeString('it-IT', { hour: '2-digit', minute: '2-digit' })}</td>
                              <td className="p-3"><div className="font-medium">{o.customer?.customer_name}</div><div className="text-xs text-stone-500">{o.customer?.company}</div><CreditCell o={o} /></td>
                              <td className="p-3 text-xs">{o.agent_name}</td>
                              <td className="p-3 text-center">{o.items?.length || 0}</td>
                              <td className="p-3 text-right">
                                <div>{fm(((o.grand_total ?? o.total_amount) || 0))} <span className="text-[9px] text-stone-400">IVA incl.</span></div>
                                <div className="text-xs text-stone-500">{fm(((o.subtotal_amount ?? o.total_amount) || 0))} <span className="text-[9px] text-stone-400">IVA escl.</span></div>
                              </td>
                              <td className="p-3 text-right font-semibold text-[#8A1C2A]">
                                {o.final_total_amount != null ? (
                                  <>
                                    <div>{fm(((o.final_grand_total ?? o.final_total_amount) || 0))} <span className="text-[9px] font-normal text-stone-400">IVA incl.</span></div>
                                    <div className="text-xs font-normal text-stone-500">{fm(((o.final_subtotal_amount ?? o.final_total_amount) || 0))} <span className="text-[9px] text-stone-400">IVA escl.</span></div>
                                  </>
                                ) : "—"}
                              </td>
                              <td className="p-3 text-center text-xs">
                                {o.shipping_date ? <span className="text-stone-700">{o.shipping_date}</span> : <span className="text-stone-300">—</span>}
                                {o.requested_shipping_date && <div className="text-[10px] text-amber-700 font-semibold whitespace-nowrap" title="Data di consegna richiesta dall'agente">cons. {new Date(o.requested_shipping_date).toLocaleDateString('it-IT')}</div>}
                              </td>
                              <td className="p-3 max-w-[210px]">{deliveryMap[o.id] ? <DeliveryBadges info={deliveryMap[o.id]} testid={`wh-delivery-${o.order_number}`} /> : (secKey === "prep" ? <span className="text-stone-300">—</span> : null)}</td>
                              <td className="p-3 text-center"><Badge className={STATUS_COLOR[o.status]}>{statusLabelMap(t)[o.status]}</Badge></td>
                              <td className="p-3 text-right" onClick={(e) => e.stopPropagation()}>
                                <div className="inline-flex items-center gap-1">
                                  <Button size="sm" variant="outline" onClick={(e) => printSingleSummary(o.id, e)} className="h-8 px-2 border-stone-300" title="Stampa riepilogo etichetta" data-testid={`wh-print-summary-${o.order_number}`}>
                                    <FileText className="w-3.5 h-3.5" />
                                  </Button>
                                  <Button size="sm" variant="outline" onClick={(e) => printCustomerNameLabel(o.id, e)} className="h-8 px-2 border-stone-300" title="Stampa etichetta solo nome cliente" data-testid={`wh-print-customer-name-${o.order_number}`}>
                                    <Tag className="w-3.5 h-3.5" />
                                  </Button>
                                  <OrderRowActions
                                    order={o}
                                    onOpenDetail={(ord) => openOrder(ord)}
                                    onOpenChat={(id) => setChatOrderId(id)}
                                    emailEnabled
                                  />
                                </div>
                              </td>
                            </tr>
                            {rowNotes && (
                              <tr className={`border-b border-stone-100 cursor-pointer ${isUnread(o) ? "bg-amber-50 hover:bg-amber-100" : "hover:bg-stone-50"}`} onClick={() => openOrder(o)} data-testid={`wh-row-notes-${o.order_number}`}>
                                <td></td>
                                <td colSpan={11} className="px-3 pb-2.5 pt-0">
                                  <div className="flex flex-wrap gap-1.5 text-[11px] leading-snug">
                                    {o.delivery_notes && <span className="inline-flex items-start gap-1 bg-amber-50 border border-amber-300 text-amber-900 rounded-md px-2 py-0.5" data-testid={`wh-note-head-${o.order_number}`}><span>📝</span><span><strong>Agente · testata (consegna):</strong> {o.delivery_notes}</span></span>}
                                    {footNotes && <span className="inline-flex items-start gap-1 bg-yellow-50 border border-yellow-400 text-yellow-900 rounded-md px-2 py-0.5 whitespace-pre-wrap" data-testid={`wh-note-foot-${o.order_number}`}><span>🗒️</span><span><strong>Agente · fondo (per il magazzino):</strong> {footNotes}</span></span>}
                                    {o.warehouse_notes && <span className="inline-flex items-start gap-1 bg-blue-50 border border-blue-300 text-blue-900 rounded-md px-2 py-0.5"><span>🏭</span><span><strong>Note magazzino:</strong> {o.warehouse_notes}</span></span>}
                                    {o.internal_notes && <span className="inline-flex items-start gap-1 bg-stone-100 border border-stone-300 text-stone-700 rounded-md px-2 py-0.5"><span>🔒</span><span><strong>Interne:</strong> {o.internal_notes}</span></span>}
                                  </div>
                                </td>
                              </tr>
                            )}
                            </React.Fragment>
                          );
                        }),
                      ];
                    })}
                  </tbody>
                </table>
                </div>
  );

  const renderMobileList = (list, secKey) => (
                <div className="md:hidden divide-y divide-stone-100">
                  {groupByDate(list).flatMap(([dateKey, rows]) => {
                    const label = dateKey === "—" ? "Senza data" : new Date(dateKey).toLocaleDateString("it-IT", { weekday: "short", day: "2-digit", month: "short", year: "numeric" });
                    return [
                      <div key={`gm-${secKey}-${dateKey}`} className="bg-stone-100 border-y-2 border-stone-300 px-4 py-2 text-xs font-bold uppercase tracking-wide text-stone-700 sticky top-0 z-10" data-testid={`wh-date-group-mobile-${secKey}-${dateKey}`}>
                        📅 {label} <span className="text-stone-500 font-normal">· {rows.length} {rows.length === 1 ? "ordine" : "ordini"}</span>
                      </div>,
                      ...rows.map(o => (
                          <div key={o.id} className={`p-4 ${isUnread(o) ? "bg-amber-50 hover:bg-amber-100" : "hover:bg-stone-50"}`} data-testid={`warehouse-order-card-${o.order_number}`}>
                            <div className="flex items-start gap-2">
                              <input
                                type="checkbox"
                                checked={selectedIds.has(o.id)}
                                onChange={() => toggleSelectOne(o.id)}
                                className="w-5 h-5 accent-stone-900 mt-1"
                                data-testid={`wh-select-mobile-${o.order_number}`}
                                onClick={(e) => e.stopPropagation()}
                              />
                              <div className="flex-1 cursor-pointer" onClick={() => openOrder(o)}>
                                <div className="flex items-center justify-between mb-2 flex-wrap gap-2">
                                  <span className="font-mono font-semibold text-[#8A1C2A] text-sm">
                                    {o.order_number}
                                    {isUnread(o) && <span className="ml-1.5 inline-flex items-center bg-amber-400 text-stone-900 text-[9px] font-bold px-1.5 py-0.5 rounded-full animate-pulse align-middle" data-testid={`wh-unread-mobile-${o.order_number}`}>NUOVO</span>}
                                    {hasCR(o) && <span className="ml-1.5 inline-flex items-center bg-purple-600 text-white text-[9px] font-bold px-1.5 py-0.5 rounded-full animate-pulse align-middle" data-testid={`wh-cr-mobile-${o.order_number}`}>🔁 MODIFICA</span>}
                                    <DdtBadge o={o} mobile />
                                  </span>
                                  <Badge className={STATUS_COLOR[o.status]}>{statusLabelMap(t)[o.status]}</Badge>
                                </div>
                                <p className="text-sm font-medium">{o.customer?.customer_name}</p>
                                <p className="text-xs text-stone-500">Agente: {o.agent_name} · {o.items?.length || 0} art.</p>
                                <CreditCell o={o} />
                                {(o.delivery_notes || o.customer?.special_requests || o.warehouse_notes || o.internal_notes) && (
                                  <div className="mt-1.5 space-y-1" data-testid={`wh-card-notes-${o.order_number}`}>
                                    {o.delivery_notes && <p className="text-[11px] bg-amber-50 border-l-2 border-amber-400 px-2 py-1 rounded text-amber-900" data-testid={`wh-note-head-mobile-${o.order_number}`}>📝 <strong>Agente · testata:</strong> {o.delivery_notes}</p>}
                                    {o.customer?.special_requests && <p className="text-[11px] bg-yellow-50 border-l-2 border-yellow-500 px-2 py-1 rounded text-yellow-900 whitespace-pre-wrap" data-testid={`wh-note-foot-mobile-${o.order_number}`}>🗒️ <strong>Agente · fondo:</strong> {o.customer.special_requests}</p>}
                                    {o.warehouse_notes && <p className="text-[11px] bg-blue-50 border-l-2 border-blue-400 px-2 py-1 rounded text-blue-900">🏭 <strong>Magazzino:</strong> {o.warehouse_notes}</p>}
                                    {o.internal_notes && <p className="text-[11px] bg-stone-100 border-l-2 border-stone-400 px-2 py-1 rounded text-stone-700">🔒 {o.internal_notes}</p>}
                                  </div>
                                )}
                                <div className="flex items-center justify-between mt-2 text-sm">
                                  <span className="text-stone-600 text-xs">{new Date(o.created_at).toLocaleTimeString('it-IT', { hour: '2-digit', minute: '2-digit' })}</span>
                                  <span className="text-right">
                                    <span className="font-semibold text-[#8A1C2A] block">{fm(((o.final_grand_total ?? o.grand_total ?? o.total_amount) || 0))} <span className="text-[9px] font-normal text-stone-400">IVA incl.</span></span>
                                    <span className="text-xs text-stone-500">{fm(((o.final_subtotal_amount ?? o.subtotal_amount ?? o.total_amount) || 0))} <span className="text-[9px] text-stone-400">IVA escl.</span></span>
                                  </span>
                                </div>
                                {o.requested_shipping_date && <p className="text-xs text-amber-700 font-semibold mt-1">🚚 Consegna richiesta: {new Date(o.requested_shipping_date).toLocaleDateString('it-IT')}</p>}
                                {o.shipping_date && <p className="text-xs text-stone-600 mt-1">🚚 {o.shipping_date}</p>}
                                {deliveryMap[o.id] && <div className="mt-1.5"><DeliveryBadges info={deliveryMap[o.id]} testid={`wh-delivery-mobile-${o.order_number}`} /></div>}
                              </div>
                            </div>
                            <div className="mt-2 flex justify-end gap-1" onClick={(e) => e.stopPropagation()}>
                              <Button size="sm" variant="outline" onClick={(e) => printSingleSummary(o.id, e)} className="h-8 px-2 border-stone-300" title="Riepilogo">
                                <FileText className="w-3.5 h-3.5" />
                              </Button>
                              <OrderRowActions
                                order={o}
                                onOpenDetail={(ord) => openOrder(ord)}
                                onOpenChat={(id) => setChatOrderId(id)}
                                emailEnabled
                              />
                            </div>
                          </div>
                      )),
                    ];
                  })}
                </div>
  );

  return (
    <div className="max-w-7xl mx-auto p-4 lg:p-6">
        <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
          <div>
            <h1 className="text-xl sm:text-2xl font-bold text-stone-900">{t.pWh_orderHeader}</h1>
            <p className="text-stone-500 text-sm"><strong className="text-amber-700">{prepOrders.length} da preparare</strong> · {orders.length} totali</p>
          </div>
          <InstallPWAButton />
        </div>

        <div className="flex flex-col sm:flex-row flex-wrap gap-3 mb-4">
          <div className="relative flex-1 max-w-md">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-stone-400" />
            <Input placeholder={t.pWh_searchOrder} value={search} onChange={e => setSearch(e.target.value)} className="pl-10 bg-white border-stone-300" data-testid="warehouse-search" />
          </div>
          <Button onClick={() => setDdtUploadOpen(true)} variant="outline" className="border-[#8A1C2A] text-[#8A1C2A] hover:bg-[#8A1C2A]/10" data-testid="wh-ddt-standalone-btn">
            <ScanLine className="w-4 h-4 mr-2" /> Carica DDT senza ordine
          </Button>
          {selectedIds.size > 0 && (
            <Button onClick={printBatchSummary} disabled={printing} className="bg-stone-900 hover:bg-black text-white" data-testid="wh-batch-print-summary-btn">
              <FileText className="w-4 h-4 mr-2" /> {printing ? "Generazione..." : `Stampa riepilogo (${selectedIds.size})`}
            </Button>
          )}
        </div>

        {/* ====== DA PREPARARE — elenco completo sempre visibile in cima ====== */}
        <div className="mb-8" data-testid="wh-prep-section">
          <div className="flex items-center justify-between bg-amber-400 rounded-t-xl px-4 py-3 border-2 border-amber-500 border-b-0">
            <h2 className="text-sm sm:text-base font-extrabold uppercase tracking-wide text-stone-900 flex items-center gap-2">
              📦 Da preparare
              <span className="bg-stone-900 text-white text-xs font-bold px-2.5 py-1 rounded-full" data-testid="wh-prep-count">{prepOrders.length}</span>
            </h2>
            <span className="hidden sm:block text-[11px] font-semibold text-amber-900">Ordini in attesa di preparazione</span>
          </div>
          <Card className="bg-white border-2 border-amber-500 border-t-0 rounded-t-none overflow-hidden">
            {loading ? <div className="p-12 text-center"><div className="spinner w-6 h-6 mx-auto" /></div>
              : prepOrders.length === 0 ? (
                <div className="p-10 text-center text-stone-500" data-testid="wh-prep-empty">
                  <CheckCircle2 className="w-10 h-10 mx-auto mb-2 text-emerald-400" />
                  <p className="font-medium">Nessun ordine da preparare 🎉</p>
                </div>
              ) : (
                <>
                  {renderDesktopTable(prepOrders, "prep")}
                  {renderMobileList(prepOrders, "prep")}
                </>
              )}
          </Card>
        </div>

        {/* ====== SPEDITI / CONCLUSI ====== */}
        <div data-testid="wh-done-section">
          <div className="flex flex-wrap items-center justify-between gap-2 bg-stone-200 rounded-t-xl px-4 py-2.5 border border-stone-300 border-b-0">
            <h2 className="text-xs sm:text-sm font-extrabold uppercase tracking-wide text-stone-700">
              🚚 Spediti / Conclusi <span className="font-normal">· {doneOrders.length}</span>
            </h2>
            <Select value={filter} onValueChange={setFilter}>
              <SelectTrigger className="w-full sm:w-56 h-8 bg-white border-stone-300 text-xs" data-testid="warehouse-filter"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="active">🚚 Spediti (in corso)</SelectItem>
                <SelectItem value="all">Tutti (incluso archivio)</SelectItem>
                <SelectItem value="shipped">🚚 Spedito</SelectItem>
                <SelectItem value="delivered">📦 Consegnato</SelectItem>
                <SelectItem value="completed">🏁 Completato</SelectItem>
                <SelectItem value="cancelled">❌ Annullato</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <Card className="bg-white border-stone-200 rounded-t-none overflow-hidden">
            {loading ? <div className="p-12 text-center"><div className="spinner w-6 h-6 mx-auto" /></div>
              : doneOrders.length === 0 ? (
                <div className="p-10 text-center text-stone-400 text-sm" data-testid="wh-done-empty">
                  <Truck className="w-8 h-8 mx-auto mb-2 text-stone-300" />
                  <p>Nessun ordine in questo stato</p>
                </div>
              ) : (
                <>
                  {renderDesktopTable(doneOrders, "done")}
                  {renderMobileList(doneOrders, "done")}
                </>
              )}
          </Card>
        </div>

        {selected && <OrderEditModal order={selected} onClose={() => setSelected(null)} onSaved={() => { setSelected(null); load(); }} onOpenChat={(id) => setChatOrderId(id)} onAdvanceCleared={(oid) => setOrders((prev) => prev.map((x) => (x.id === oid ? { ...x, advance_payment_required: false, advance_payment_cleared: true } : x)))} />}
        <DdtStandaloneDialog open={ddtUploadOpen} onClose={() => setDdtUploadOpen(false)} onCreated={load} />
        <ChatDrawer open={!!chatOrderId} onClose={() => setChatOrderId(null)} initialOrderId={chatOrderId} />
    </div>
  );
};

const WarehouseStats = () => {
  const fm = useFmt();
  const [period, setPeriod] = useState("month");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    axios.get(`${API}/warehouse/stats?period=${period}`).then(r => setData(r.data)).catch(() => toast.error("Errore stats")).finally(() => setLoading(false));
  }, [period]);

  const KPI = ({ label, value, sub, icon: Icon, color = "text-[#8A1C2A]" }) => (
    <Card className="bg-white border-stone-200 p-4">
      <div className="flex items-start justify-between">
        <div>
          <p className="text-[10px] text-stone-500 uppercase tracking-wide">{label}</p>
          <p className={`text-2xl font-bold mt-1 ${color}`}>{value}</p>
          {sub && <p className="text-xs text-stone-500 mt-1">{sub}</p>}
        </div>
        <Icon className="w-7 h-7 text-stone-300" />
      </div>
    </Card>
  );

  if (loading || !data) return <div className="p-8 text-center"><div className="spinner w-6 h-6 mx-auto" /></div>;
  const s = data.summary;
  return (
    <div className="max-w-7xl mx-auto p-4 lg:p-6 space-y-6">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-xl sm:text-2xl font-bold text-stone-900 flex items-center gap-2"><BarChart3 className="w-6 h-6 text-[#8A1C2A]" /> Statistiche magazzino</h1>
          <p className="text-stone-500 text-sm">KPI operativi · ordini lavorati, pezzi mancanti, prodotti spediti</p>
        </div>
        <Select value={period} onValueChange={setPeriod}>
          <SelectTrigger className="w-44 bg-white border-stone-300" data-testid="warehouse-stats-period"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="today">Oggi</SelectItem>
            <SelectItem value="week">Ultima settimana</SelectItem>
            <SelectItem value="month">Mese corrente</SelectItem>
            <SelectItem value="year">Anno corrente</SelectItem>
          </SelectContent>
        </Select>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3" data-testid="warehouse-kpi-grid">
        <KPI label="Da preparare" value={s.orders_to_prepare} icon={Package} color="text-orange-600" />
        <KPI label="Pronti" value={s.orders_ready} icon={CheckCircle2} color="text-cyan-700" />
        <KPI label="Spediti" value={s.orders_shipped} icon={Truck} color="text-emerald-700" />
        <KPI label="Totale periodo" value={s.total_orders} icon={ClipboardList} />
        <KPI label="Righe mancanti" value={s.missing_lines} icon={AlertTriangle} color="text-red-600" />
        <KPI label="Righe parziali" value={s.partial_lines} icon={AlertTriangle} color="text-yellow-600" />
        <KPI label="Kg lavorati" value={`${s.total_real_weight_kg.toFixed(1)}`} sub="solo pesi reali Kg" icon={Boxes} color="text-[#C5A059]" />
        <KPI label="Costi extra" value={`${fm(s.extra_costs_total)}`} icon={FileText} />
        <KPI label="Valore merce" value={fm(s.total_value || 0)} sub="imponibile ordini lavorati" icon={BarChart3} color="text-[#8A1C2A]" />
      </div>

      {/* Quantità per unità di misura */}
      {(data.units || []).length > 0 && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3" data-testid="warehouse-units-grid">
          {data.units.map((u) => (
            <Card key={u.unit} className="bg-white border-stone-200 p-3">
              <p className="text-[10px] text-stone-500 uppercase tracking-wide">Movimentato in {u.unit}</p>
              <p className="text-lg font-bold text-stone-900">{u.quantity.toFixed(1)} <span className="text-sm font-medium text-stone-500">{u.unit}</span></p>
              <p className="text-[10px] text-stone-400">{u.lines} righe</p>
            </Card>
          ))}
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {/* Categorie movimentate */}
        <Card className="bg-white border-stone-200 overflow-hidden" data-testid="warehouse-categories-card">
          <div className="p-4 border-b border-stone-200"><h3 className="font-semibold text-stone-900">Categorie movimentate</h3></div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-stone-50 border-b border-stone-200">
                <tr>
                  <th className="text-left p-3 text-xs font-semibold text-stone-600">Categoria</th>
                  <th className="text-right p-3 text-xs font-semibold text-stone-600">Q.tà</th>
                  <th className="text-right p-3 text-xs font-semibold text-stone-600">Valore</th>
                </tr>
              </thead>
              <tbody>
                {(data.categories || []).length === 0 ? <tr><td colSpan="3" className="p-6 text-center text-stone-500">Nessun dato</td></tr>
                  : data.categories.map((c) => (
                    <tr key={c.category} className="border-b border-stone-100">
                      <td className="p-3 font-medium">{c.category}</td>
                      <td className="p-3 text-right">{c.quantity.toFixed(1)}</td>
                      <td className="p-3 text-right font-semibold text-[#8A1C2A]">{fm(c.value)}</td>
                    </tr>
                  ))}
              </tbody>
            </table>
          </div>
        </Card>

        {/* Top agenti */}
        <Card className="bg-white border-stone-200 overflow-hidden" data-testid="warehouse-agents-card">
          <div className="p-4 border-b border-stone-200"><h3 className="font-semibold text-stone-900">Top agenti (ordini lavorati)</h3></div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-stone-50 border-b border-stone-200">
                <tr>
                  <th className="text-left p-3 text-xs font-semibold text-stone-600">Agente</th>
                  <th className="text-right p-3 text-xs font-semibold text-stone-600">Ordini</th>
                  <th className="text-right p-3 text-xs font-semibold text-stone-600">Valore</th>
                </tr>
              </thead>
              <tbody>
                {(data.top_agents || []).length === 0 ? <tr><td colSpan="3" className="p-6 text-center text-stone-500">Nessun dato</td></tr>
                  : data.top_agents.map((a) => (
                    <tr key={a.agent_name} className="border-b border-stone-100">
                      <td className="p-3 font-medium">{a.agent_name}</td>
                      <td className="p-3 text-right">{a.orders}</td>
                      <td className="p-3 text-right font-semibold text-[#8A1C2A]">{fm(a.value)}</td>
                    </tr>
                  ))}
              </tbody>
            </table>
          </div>
        </Card>
      </div>

      <Card className="bg-white border-stone-200 overflow-hidden">
        <div className="p-4 border-b border-stone-200">
          <h3 className="font-semibold text-stone-900">Top prodotti spediti</h3>
          <p className="text-xs text-stone-500">Solo quantità effettivamente uscite dal magazzino (consegnate o parziali)</p>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm min-w-[500px]">
            <thead className="bg-stone-50 border-b border-stone-200">
              <tr>
                <th className="text-left p-3 text-xs font-semibold text-stone-600">Codice</th>
                <th className="text-left p-3 text-xs font-semibold text-stone-600">Descrizione</th>
                <th className="text-right p-3 text-xs font-semibold text-stone-600">Q.tà totale</th>
                <th className="text-left p-3 text-xs font-semibold text-stone-600">U.M.</th>
                <th className="text-right p-3 text-xs font-semibold text-stone-600">Valore</th>
                <th className="text-right p-3 text-xs font-semibold text-stone-600">Ordini</th>
              </tr>
            </thead>
            <tbody>
              {data.top_products.length === 0 ? <tr><td colSpan="6" className="p-6 text-center text-stone-500">Nessun dato</td></tr>
                : data.top_products.map(p => (
                  <tr key={p.code} className="border-b border-stone-100">
                    <td className="p-3 font-mono text-xs text-[#8A1C2A]">{p.code}</td>
                    <td className="p-3">{p.description}</td>
                    <td className="p-3 text-right font-semibold">{p.quantity.toFixed(2)}</td>
                    <td className="p-3 text-xs text-stone-600">{p.unit || "Kg"}</td>
                    <td className="p-3 text-right font-semibold text-[#8A1C2A]">{fm(p.value || 0)}</td>
                    <td className="p-3 text-right">{p.orders}</td>
                  </tr>
                ))}
            </tbody>
          </table>
        </div>
      </Card>

      <Card className="bg-white border-stone-200 overflow-hidden">
        <div className="p-4 border-b border-stone-200"><h3 className="font-semibold text-stone-900">Andamento giornaliero</h3></div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm min-w-[400px]">
            <thead className="bg-stone-50 border-b border-stone-200">
              <tr>
                <th className="text-left p-3 text-xs font-semibold text-stone-600">Data</th>
                <th className="text-right p-3 text-xs font-semibold text-stone-600">Ordini</th>
                <th className="text-right p-3 text-xs font-semibold text-stone-600">Righe lavorate</th>
              </tr>
            </thead>
            <tbody>
              {data.daily.length === 0 ? <tr><td colSpan="3" className="p-6 text-center text-stone-500">Nessun dato</td></tr>
                : data.daily.map(d => (
                  <tr key={d.date} className="border-b border-stone-100">
                    <td className="p-3">{d.date}</td>
                    <td className="p-3 text-right">{d.orders}</td>
                    <td className="p-3 text-right text-[#8A1C2A] font-semibold">{d.lines_done}</td>
                  </tr>
                ))}
            </tbody>
          </table>
        </div>
      </Card>
    </div>
  );
};

const WarehouseSidebar = ({ user, logout, navItems, mobileOpen, setMobileOpen, collapsed, onToggleCollapsed, t }) => {
  const loc = useLocation();
  const navigate = useNavigate();
  const body = (
    <>
      <div className="p-6 border-b border-stone-200 flex items-center justify-between">
        <Logo />
        <button className="hidden lg:flex p-1 text-stone-400 hover:text-stone-700" onClick={onToggleCollapsed} title="Comprimi menu" data-testid="warehouse-sidebar-collapse"><PanelLeftClose className="w-5 h-5" /></button>
        <button className="lg:hidden p-1 text-stone-500" onClick={() => setMobileOpen(false)} data-testid="warehouse-sidebar-close"><X className="w-5 h-5" /></button>
      </div>
      <div className="px-6 py-3 border-b border-stone-200">
        <p className="text-xs text-stone-500">{user?.role === "admin" ? `${t.admin} · ${t.warehouse}` : `${t.warehouse}`}</p>
        <p className="font-semibold text-stone-900 truncate">{user?.name}</p>
      </div>
      <nav className="flex-1 px-4 py-4 space-y-1 overflow-y-auto">
        {navItems.map((it) => {
          const active = loc.pathname.startsWith(it.path);
          return (
            <Link key={it.path} to={it.path} onClick={() => setMobileOpen(false)} className={`group flex items-center gap-3 px-4 py-3 rounded-lg text-sm font-medium transition-all ${active ? "bg-[#8A1C2A] text-white shadow-sm" : "text-stone-700 hover:bg-stone-100"}`} data-testid={`warehouse-nav-${it.path.split("/").pop()}`}>
              <it.icon className="w-5 h-5" />
              <span className="flex-1">{it.label}</span>
              <span
                role="button"
                title="Apri in un'altra scheda"
                onClick={(e) => { e.preventDefault(); e.stopPropagation(); window.open(it.path, "_blank"); }}
                className={`p-1 rounded opacity-50 lg:opacity-0 lg:group-hover:opacity-60 hover:!opacity-100 transition-opacity ${active ? "hover:bg-white/20" : "hover:bg-stone-200"}`}
                data-testid={`warehouse-newtab-${it.path.split("/").pop()}`}
              >
                <ExternalLink className="w-3.5 h-3.5" />
              </span>
            </Link>
          );
        })}
      </nav>
      <div className="p-4 border-t border-stone-200 space-y-1 pb-[max(1rem,env(safe-area-inset-bottom))] shrink-0">
        {user?.role === "admin" && (
          <button onClick={() => { setMobileOpen(false); navigate("/admin"); }} className="w-full flex items-center gap-3 px-3 py-2 text-sm text-[#8A1C2A] font-semibold hover:bg-[#8A1C2A]/10 rounded-md" data-testid="warehouse-back-to-admin-btn">
            <ChevronRight className="w-4 h-4 rotate-180" />Torna ad Admin
          </button>
        )}
        <button onClick={() => { logout(); navigate("/login"); }} className="w-full flex items-center gap-3 px-3 py-2 text-sm text-stone-600 hover:text-stone-900 hover:bg-stone-100 rounded-md" data-testid="warehouse-logout-btn">
          <LogOut className="w-4 h-4" />{t.logout}
        </button>
      </div>
    </>
  );
  const compactBody = (
    <>
      <div className="p-3 border-b border-stone-200 flex justify-center shrink-0">
        <button onClick={onToggleCollapsed} className="p-2 rounded-md text-stone-500 hover:text-stone-900 hover:bg-stone-100" title="Espandi menu" data-testid="warehouse-sidebar-expand"><PanelLeftOpen className="w-5 h-5" /></button>
      </div>
      <nav className="flex-1 px-2 py-3 space-y-1 overflow-y-auto">
        {navItems.map((it) => {
          const active = loc.pathname.startsWith(it.path);
          return (
            <Link key={it.path} to={it.path} title={it.label} className={`relative flex items-center justify-center p-2.5 rounded-lg transition-all ${active ? "bg-[#8A1C2A] text-white shadow-sm" : "text-stone-600 hover:bg-stone-100"}`} data-testid={`warehouse-nav-collapsed-${it.path.split("/").pop()}`}>
              <it.icon className="w-5 h-5" />
            </Link>
          );
        })}
      </nav>
      <div className="p-2 border-t border-stone-200 flex flex-col items-center gap-1 pb-[max(0.5rem,env(safe-area-inset-bottom))] shrink-0">
        {user?.role === "admin" && (
          <button onClick={() => navigate("/admin")} className="p-2 text-[#8A1C2A] hover:bg-[#8A1C2A]/10 rounded-md" title="Torna ad Admin" data-testid="warehouse-back-to-admin-collapsed">
            <ChevronRight className="w-4 h-4 rotate-180" />
          </button>
        )}
        <button onClick={() => { logout(); navigate("/login"); }} className="p-2 text-stone-500 hover:bg-stone-100 rounded-md" title={t.logout} data-testid="warehouse-logout-collapsed"><LogOut className="w-4 h-4" /></button>
      </div>
    </>
  );
  return (
    <>
      <aside className={`hidden lg:flex fixed left-0 top-0 bottom-0 ${collapsed ? "w-[64px]" : "w-64"} bg-white border-r border-stone-200 flex-col z-30 transition-[width] duration-200`}>
        {collapsed ? compactBody : body}
      </aside>
      {mobileOpen && (
        <div className="fixed inset-0 z-40 lg:hidden">
          <div className="absolute inset-0 bg-black/40" onClick={() => setMobileOpen(false)} />
          <aside className="absolute left-0 top-0 bottom-0 w-72 max-w-[85vw] bg-white border-r border-stone-200 flex flex-col">{body}</aside>
        </div>
      )}
    </>
  );
};

const WarehousePortal = ({ user, logout }) => {
  const { t } = useLang();
  const [chatOpen, setChatOpen] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [disabledModules, setDisabledModules] = useState(user?.role === "warehouse" ? null : []);
  const whLoc = useLocation();
  const [collapsed, setCollapsed] = useState(() => localStorage.getItem("giotto_wh_sidebar_collapsed") === "1");
  const toggleCollapsed = () => setCollapsed((c) => { localStorage.setItem("giotto_wh_sidebar_collapsed", c ? "0" : "1"); return !c; });
  useEffect(() => {
    if (user?.role !== "warehouse") return;
    axios.get(`${API}/my-modules`)
      .then((r) => setDisabledModules(r.data?.disabled_modules || []))
      .catch(() => setDisabledModules([]));
  }, [user]);
  const navItems = [
    { path: "/warehouse/orders", label: t.orders, icon: Package },
    { path: "/warehouse/logistics", label: "Logistica", icon: Truck },
    { path: "/warehouse/inventory", label: "Carichi Magazzino", icon: PackagePlus },
    { path: "/warehouse/lots", label: "Giacenze & Partite", icon: Boxes },
    { path: "/warehouse/wms", label: "WMS · Giacenze", icon: Boxes },
    { path: "/warehouse/wms-movimenti", label: "WMS · Movimenti", icon: ArrowRightLeft },
    { path: "/warehouse/documents", label: "Documenti", icon: FolderOpen },
    { path: "/warehouse/statements", label: "Scadenziario", icon: Receipt },
    { path: "/warehouse/customers", label: t.customers || "Clienti", icon: Building2 },
    { path: "/warehouse/pricelists", label: "Listini", icon: FileText },
    { path: "/warehouse/offers", label: t.offers, icon: Sparkles },
    { path: "/warehouse/dedicated-offers", label: "Offerte Dedicate", icon: Share2 },
    { path: "/warehouse/suppliers", label: t.suppliers, icon: Building2 },
    ...(user?.role === "admin" || user?.purchases_enabled ? [{ path: "/purchases/suppliers", label: "Acquisti", icon: ShoppingCart }] : []),
    { path: "/warehouse/stats", label: t.pWh_stats, icon: BarChart3 },
    { path: "/warehouse/tutorials", label: "Guide", icon: Video },
    { path: "/warehouse/mail", label: "Mail", icon: Inbox },
    { path: "/warehouse/settings", label: "Impostazioni", icon: SettingsIcon },
  ].filter((it) => !(disabledModules || []).includes(it.path.split("/").pop()));
  const whSeg = whLoc.pathname.split("/")[2] || "";

  return (
    <div className="min-h-screen bg-stone-50">
      {/* Modulo disattivato dall'admin → reindirizza al primo modulo consentito */}
      {Array.isArray(disabledModules) && disabledModules.includes(whSeg) && navItems.length > 0 && (
        <Navigate to={navItems[0].path} replace />
      )}
      <WarehouseSidebar user={user} logout={logout} navItems={navItems} mobileOpen={mobileOpen} setMobileOpen={setMobileOpen} collapsed={collapsed} onToggleCollapsed={toggleCollapsed} t={t} />
      <div className={collapsed ? "lg:pl-[64px]" : "lg:pl-64"}>
        {/* Mobile header */}
        <div className="lg:hidden sticky top-0 z-20 bg-white border-b border-stone-200 px-4 py-3 flex items-center justify-between">
          <button onClick={() => setMobileOpen(true)} className="p-2 -ml-2 text-stone-700" data-testid="warehouse-mobile-menu-btn">
            <Menu className="w-5 h-5" />
          </button>
          <Logo />
          <div className="flex items-center gap-1">
            <InstallPWAButton compact={true} />
            <ChatButton onClick={() => setChatOpen(true)} data-testid="warehouse-chat-btn-mobile" />
          </div>
        </div>
        {/* Desktop header */}
        <div className="hidden lg:flex sticky top-0 z-20 bg-white border-b border-stone-200 px-6 py-2 items-center justify-end gap-1">
          <InstallPWAButton compact={true} />
          <ChatButton onClick={() => setChatOpen(true)} data-testid="warehouse-chat-btn-desktop" />
        </div>

        <Routes>
          <Route path="/orders" element={<OrdersToPrepare />} />
          <Route path="/logistics" element={<div className="max-w-7xl mx-auto p-4 lg:p-6"><LogisticsPage canManage /></div>} />
          <Route path="/inventory" element={<div className="max-w-7xl mx-auto p-4 lg:p-6"><InventoryShipments /></div>} />
          <Route path="/lots" element={<div className="max-w-7xl mx-auto p-4 lg:p-6"><InventoryLots /></div>} />
          <Route path="/wms" element={<div className="max-w-7xl mx-auto p-4 lg:p-6"><WmsGiacenze /></div>} />
          <Route path="/wms-movimenti" element={<div className="max-w-7xl mx-auto p-4 lg:p-6"><WmsMovimenti /></div>} />
          <Route path="/documents" element={<div className="max-w-7xl mx-auto p-4 lg:p-6"><DocumentsPage currentUser={user} company={null} /></div>} />
          <Route path="/statements" element={<div className="max-w-7xl mx-auto p-4 lg:p-6"><StatementsPage currentUser={user} company={null} /></div>} />
          <Route path="/customers" element={<div className="max-w-7xl mx-auto p-4 lg:p-6"><AdminCustomersManagement /></div>} />
          <Route path="/pricelists" element={<div className="max-w-7xl mx-auto p-4 lg:p-6"><WarehousePricelistsView /></div>} />
          <Route path="/offers" element={<WarehouseOffersTab />} />
          <Route path="/dedicated-offers" element={<div className="max-w-7xl mx-auto p-4 lg:p-6"><DedicatedOffersList currentUser={user} /></div>} />
          <Route path="/suppliers" element={<div className="max-w-7xl mx-auto p-4 lg:p-6"><SuppliersTab canEdit={true} canDelete={user?.role === "admin"} /></div>} />
          <Route path="/supplier-orders" element={<Navigate to="/purchases/orders" replace />} />
          <Route path="/stats" element={<WarehouseStats />} />
          <Route path="/tutorials" element={<div className="max-w-7xl mx-auto p-4 lg:p-6"><TutorialsPage currentUser={user} /></div>} />
          <Route path="/mail" element={<div className="max-w-7xl mx-auto p-4 lg:p-6"><WebmailPage /></div>} />
          <Route path="/settings" element={<div className="max-w-7xl mx-auto p-4 lg:p-6"><div className="max-w-3xl"><EmailSettingsCard /></div></div>} />
          <Route path="*" element={<Navigate to="/warehouse/orders" />} />
        </Routes>
      </div>

      <ChatDrawer open={chatOpen} onClose={() => setChatOpen(false)} />
    </div>
  );
};

export default WarehousePortal;
